import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import {
  Sparkles, Upload, ClipboardPaste, Keyboard, FileCheck2, Printer, FileDown, Tag,
  ArrowLeft, Hammer, SearchCheck, CircleCheck,
} from 'lucide-react';
import { api } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { PipelineRail, RailRow } from '../components/pipeline/PipelineRail';
import DeskPaper, { ReviewPaper, printDoc, printNft, downloadWord } from './desk/DeskPaper';
import { parseMaterialsXlsx } from './desk/deskImport';
import { extractFileText, ACCEPT_DOCS } from '../lib/fileText';
import './desk/desk.css';

// The desk — fa7's click-verified design on the app's own components (CLAUDE.md §7 locked
// decisions): one entry surface for the intake, the C-view (form beside its source, click a value
// to see its sentence), the checklist rendered INSIDE the rail via PipelineRail/RailRow, the
// factory strip always visible during a run, one run button, and the gate's three verbs.

const FACTORY = ['order', 'materials', 'compute', 'check', 'gate', 'doc'];

// N-139: an estimate that is always wrong is a small lie - remember the last runs and show the
// measured median instead of a constant.
const etaRecord = (kind, seconds) => {
  try {
    const k = 'desk_eta_' + kind;
    const arr = JSON.parse(localStorage.getItem(k) || '[]');
    arr.push(seconds);
    localStorage.setItem(k, JSON.stringify(arr.slice(-10)));
  } catch { /* telemetry only */ }
};
const etaMedian = (kind) => {
  try {
    const arr = JSON.parse(localStorage.getItem('desk_eta_' + kind) || '[]');
    if (arr.length < 2) return null;
    const sorted = [...arr].sort((a, b) => a - b);
    return Math.round(sorted[Math.floor(sorted.length / 2)]);
  } catch { return null; }
};

function Assistant({ children }) {
  return (
    <div className="mb-4 flex items-start gap-3">
      <span className="flex h-9 w-9 flex-none items-center justify-center rounded-full bg-gradient-to-br from-primary to-violet-500 text-primary-foreground shadow">
        <Sparkles className="h-4 w-4" />
      </span>
      <div className="rounded-2xl rounded-ss-sm border border-border bg-card px-4 py-3 text-sm leading-7 shadow-sm">
        {children}
      </div>
    </div>
  );
}

function FactoryStrip({ phase, gate }) {
  const { t } = useTranslation();
  const idx = FACTORY.indexOf(phase);
  return (
    <div className="desk-factory mb-3" data-testid="desk-factory">
      <div className="mb-2 text-[11px] opacity-70">{t('desk.factory.title')}</div>
      <div className="df-belt">
        <div className={`df-rail ${idx >= 0 && !gate ? 'run' : ''}`} />
        {FACTORY.map((k, i) => (
          <div
            key={k}
            className={`df-st ${i === idx ? (gate && k === 'gate' ? 'gate' : 'act') : ''}`}
            style={{ insetInlineStart: `${6 + i * 15.5}%` }}
          >
            <div className="df-bx">{t(`desk.factory.${k}`)}</div>
          </div>
        ))}
        <div className="df-crate" style={{ insetInlineStart: `${Math.max(2, 6 + idx * 15.5)}%` }}>
          {t('desk.factory.crate')}
        </div>
      </div>
    </div>
  );
}

export default function DeskPage() {
  const { t } = useTranslation();
  const [view, setView] = useState('home'); // home | order | run | bench
  const [pack, setPack] = useState(null);
  const [industries, setIndustries] = useState([]);
  const [corpora, setCorpora] = useState([]); // CorpusSelection[]
  const [materials, setMaterials] = useState(null); // {version_no,row_count,gap_count,filename}
  const [checklistOff, setChecklistOff] = useState([]);
  // order state
  const [intakeMode, setIntakeMode] = useState(null); // up | paste | hand
  const [docText, setDocText] = useState('');
  const [fields, setFields] = useState(null); // [{key,value,confidence,source_snippet}]
  const [formula, setFormula] = useState([{ code: '', pct: '' }]);
  const [focusSnippet, setFocusSnippet] = useState(null);
  const [extracting, setExtracting] = useState(false);
  const [extractSec, setExtractSec] = useState(0);
  const [gateFix, setGateFix] = useState(null);   // { item_key, field, value } — inline fix editor
  const [matFix, setMatFix] = useState(null);
  // N-109 batch editor: { item_key, attr, rows: [{code, name, value}], suggesting }
  const [batchFix, setBatchFix] = useState(null);
  // N-111: never let a model think anonymously — whoever served, say so on screen
  const [servedModel, setServedModel] = useState(null);     // { item_key, code, name, allergen } — inline material editor
  const [rerunning, setRerunning] = useState(null); // Set of item_keys being re-evaluated
  const [extraOpen, setExtraOpen] = useState({});
  // targets arrive language-free from the server; the UI names them in ITS language
  const attrLabel = (attr) => t(`desk.attr.${attr}`, { defaultValue: attr });
  const targetLabel = (tg) => (tg.kind === 'intake'
    ? (schemaFields.find((x) => x.key === tg.field)?.label || tg.field)
    : `${tg.code} · ${attrLabel(tg.attr)}`);
  const [runParked, setRunParked] = useState(false);
  const [previewDoc, setPreviewDoc] = useState(null);
  const [labGoal, setLabGoal] = useState('');
  const [labCreativity, setLabCreativity] = useState('conservative');
  const [labBusy, setLabBusy] = useState(false);
  const [labCands, setLabCands] = useState(null);
  const [labOpenFindings, setLabOpenFindings] = useState({});
  const [labPre, setLabPre] = useState(null);      // pre-flight refusal: {ready, excluded}
  const [labHouse, setLabHouse] = useState(null);  // phase5: {products, lines} from signed history
  const [labFilling, setLabFilling] = useState(false);
  const [labStep, setLabStep] = useState(null); // fill | compose - the progress SAYS what runs
  const labLastSug = useRef(null); // LAYER 2: last lab proposal, survives use/dismiss
  const [labFillReport, setLabFillReport] = useState(null); // {filled:n, unfilled:[{code,attr}]}
  const [labResult, setLabResult] = useState(null); // N-127: the best candidate, applied to the box
  // run state
  const [orderId, setOrderId] = useState(null);
  const [run, setRun] = useState(null); // run_check response
  const [running, setRunning] = useState(false);
  const [signed, setSigned] = useState(null); // sign response artifact
  const [orders, setOrders] = useState([]);
  const [benchDoc, setBenchDoc] = useState(null);
  const [kind, setKind] = useState('build'); // build | review
  const [suggestions, setSuggestions] = useState(null); // extra checklist proposals
  const [suggesting, setSuggesting] = useState(false);
  const [extraOn, setExtraOn] = useState({});
  // 075/N-112 — the check LIBRARY: the user's own rows beside the pack's, each with provenance.
  const [userChecks, setUserChecks] = useState([]);
  const [userFields, setUserFields] = useState([]); // phase4: fields promoted from extras, recurring
  // absent = OFF: the pack is the baseline and your own checks are opt-in, per order or via a
  // preset. Adding a check ticks it explicitly; loading a preset sets every row explicitly.
  const [userOn, setUserOn] = useState({});
  const [presets, setPresets] = useState([]);
  const [presetName, setPresetName] = useState('');
  const [currentPreset, setCurrentPreset] = useState(null); // the preset this selection came from
  const [addCheckText, setAddCheckText] = useState('');
  const [homeSugs, setHomeSugs] = useState(null);
  const [homeSuggesting, setHomeSuggesting] = useState(false);
  const [homeSugSec, setHomeSugSec] = useState(0);
  const [manageOpen, setManageOpen] = useState(false);
  const [orderNote, setOrderNote] = useState(null);
  const [extras, setExtras] = useState([]);
  const [observations, setObservations] = useState([]);
  const [intakeWarnings, setIntakeWarnings] = useState([]); // N-115: the reading reports on itself
  const [imgReadings, setImgReadings] = useState(null);     // N-122: what the document's pictures say
  const [docKind, setDocKind] = useState(null);   // N-125: what the extractor says it read
  const [docKindNote, setDocKindNote] = useState(null); // N-135: one plain sentence about it
  const [mapFix, setMapFix] = useState(null);     // N-126: { item_key, token, choice } map picker
  const [labOpen, setLabOpen] = useState(false);  // lab invited over an EXISTING formula
  const [docOpen, setDocOpen] = useState(false);  // source-document strip expanded
  const [matRows, setMatRows] = useState(null);   // lazy list_materials rows for the picker
  const [imgReading, setImgReading] = useState(false); // {kind:'fix'|'next', text, parent}
  const [parentOrderId, setParentOrderId] = useState(null);
  const [benchLoading, setBenchLoading] = useState(null);
  const fileRef = useRef(null);
  const matsRef = useRef(null);

  useEffect(() => {
    api.desk('list_packs').then((r) => {
      const p0 = (r.packs || [])[0] || null;
      setPack(p0);
      setUserChecks(p0?.user_checks || []);
      setUserFields(p0?.user_fields || []);
      setPresets(p0?.presets || []);
    }).catch((e) => toast.error(e.message));
    api.desk('list_orders').then((r) => setOrders(r.orders || [])).catch(() => {});
  }, []);
  useEffect(() => {
    if (!pack) return;
    api.listIndustries(pack.lang).then((r) => {
      const list = r.industries || [];
      setIndustries(list);
      // Default selection: every platform corpus as the law lane, own corpora as the own lane —
      // the user unticks, the machine proposes.
      setCorpora(list.map((i) => ({
        connection_id: i.connection_id ?? null, dataset_ids: i.dataset_ids || [],
        label: i.is_platform ? 'law' : 'own', name: i.industry_label || i.industry_key, on: true,
      })));
    }).catch(() => setIndustries([]));
  }, [pack]);

  const schemaFields = useMemo(
    () => (pack?.intake_schema || []).filter((f) => f.type !== 'formula'),
    [pack],
  );
  // phase4: every field the desk knows - the pack's baseline plus the user's promoted ones
  const allFieldDefs = useMemo(
    () => [...schemaFields, ...userFields.map((f) => ({ key: f.key, label: f.label }))],
    [schemaFields, userFields],
  );
  // lanes must be unique; collapse to law + own-N
  const laneSelections = useMemo(() => {
    const out = []; let ownN = 0;
    for (const c of corpora.filter((x) => x.on && x.dataset_ids.length)) {
      out.push({ connection_id: c.connection_id, dataset_ids: c.dataset_ids, label: c.label === 'law' ? (out.some((o) => o.label === 'law') ? `law${out.length}` : 'law') : `own${ownN++ || ''}` });
    }
    return out;
  }, [corpora]);

  const importMaterials = async (file) => {
    try {
      const map = pack?.doc_template?.import_map;
      const { rows, skipped } = await parseMaterialsXlsx(file, map);
      if (!rows.length) { toast.error(t('desk.sources.importEmpty')); return; }
      const r = await api.desk('import_materials', { filename: file.name, rows });
      setMaterials({ version_no: r.version_no, row_count: r.row_count, gap_count: r.gap_count, filename: file.name });
      toast.success(t('desk.sources.imported', { rows: r.row_count, gaps: r.gap_count, skipped }));
    } catch (e) { toast.error(e.message); }
  };

  useEffect(() => {
    if (view !== 'bench' || benchDoc || benchLoading) return;
    const latest = orders.find((o) => o.run?.status === 'signed');
    if (!latest) return;
    setBenchLoading(latest.id);
    api.desk('get_state', { order_id: latest.id })
      .then((st) => { if (st.artifact) setBenchDoc(st.artifact); })
      .catch(() => {})
      .finally(() => setBenchLoading(null));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view, orders]);

  useEffect(() => {
    if (!extracting) { setExtractSec(0); return undefined; }
    const id = setInterval(() => setExtractSec((x) => x + 1), 1000);
    return () => clearInterval(id);
  }, [extracting]);

  useEffect(() => {
    if (!homeSuggesting) { setHomeSugSec(0); return undefined; }
    const id = setInterval(() => setHomeSugSec((x) => x + 1), 1000);
    return () => clearInterval(id);
  }, [homeSuggesting]);

  const extract = async (text, images = []) => {
    const t0 = Date.now();
    setExtracting(true);
    setImgReadings(null);
    try {
      const r = await api.desk('extract_intake', { pack_key: pack.key, document_text: text });
      // phase4 provenance: freeze what the DOCUMENT said - every later edit shows against this
      setFields((r.fields || []).map((f) => ({ ...f, extracted: f.value })));
      setExtras((r.extras || []).map((x) => ({ ...x, on: true })));
      setObservations(r.observations || []);
      setIntakeWarnings(r.intake_warnings || []);
      setServedModel(r.model_id || null);
      setDocKind(r.document_kind || null);
      setDocKindNote(r.kind_note || null);
      if (Array.isArray(r.formula) && r.formula.length) {
        setFormula(r.formula.map((l) => ({ code: l.code, pct: String(l.pct) })));
      }
    } catch (e) { toast.error(e.message); } finally { setExtracting(false); etaRecord('extract', Math.round((Date.now() - t0) / 1000)); }
    if (images.length) {
      setImgReading(true);
      try {
        const ir = await api.desk('read_document_images', { pack_key: pack.key, images });
        setImgReadings(ir.readings || []);
        if (ir.served_models?.length) setServedModel(ir.served_models.join(' · '));
      } catch (e) {
        // N-128: an unreadable picture is a WARNING, never a wall - the flow continues and the
        // honest line says what to do instead.
        setImgReadings([{ kind: 'unreadable', description: e.message, rows: [] }]);
      } finally { setImgReading(false); }
    }
  };

  const startRun = async () => {
    try {
      setRunning(true); setView('run'); setRun(null); setSigned(null);
      const intake = Object.fromEntries((fields || []).map((f) => [f.key, f.value]));
      intake.serving_ml = Number(intake.serving_ml) || Number(String(intake.pack_format || '').match(/(\d+)\s*ml/i)?.[1]) || null;
      intake.specific_gravity = Number(intake.specific_gravity) || 1.0;
      intake.claims = typeof intake.claims === 'string'
        ? intake.claims.split(/[·,;\n]/).map((s) => s.trim()).filter(Boolean)
        : (intake.claims || []);
      intake.extras = extras.filter((x) => x.on && String(x.value).trim())
        .map((x) => ({ label: x.label, value: x.value }));
      intake.formula = formula
        .map((l) => ({ code: String(l.code).trim(), pct: Number(l.pct) }))
        .filter((l) => l.code && Number.isFinite(l.pct));
      let oid = orderId;
      if (!oid) {
        const extra = (suggestions || []).filter((x) => extraOn[x.item_key]);
        const lib = selectedUserChecks.map((c) => ({ item_key: c.item_key, title: c.title, query_hint: c.query_hint || c.title }));
        const merged = [...lib, ...extra.map((x) => ({ item_key: x.item_key, title: x.title, query_hint: x.query_hint }))]
          .filter((x, i, arr) => arr.findIndex((y) => y.item_key === x.item_key) === i);
        const fieldProvenance = Object.fromEntries((fields || [])
          .filter((f) => f.extracted != null || f.source_snippet)
          .map((f) => [f.key, { extracted: f.extracted ?? null, snippet: f.source_snippet ?? null }]));
        const o = await api.desk('create_order', {
          pack_key: pack.key, kind, intake,
          document_text: kind === 'review' ? docText : undefined,
          corpora: laneSelections, checklist_off: checklistOff,
          document_kind: docKind || undefined,
          lab_suggestion: labLastSug.current || undefined,
          extra_checklist: merged,
          field_provenance: fieldProvenance,
          image_readings: imgReadings || [],
          custom_fields: (fields || []).filter((f) => f.custom).map((f) => ({ key: f.key, label: f.label })),
          parent_order_id: parentOrderId,
        });
        oid = o.order_id; setOrderId(oid);
        // an AI suggestion the user KEPT is a library row from now on — saved once, reusable
        extra.forEach((x) => { keepCheck(x.title, x.query_hint, 'ai').catch(() => {}); });
      }
      const r = await api.desk('run_check', { order_id: oid });
      setRun(r);
      if (r.served_models?.length) setServedModel(r.served_models.join(' · '));
    } catch (e) { toast.error(e.message); setView('order'); } finally { setRunning(false); }
  };

  useEffect(() => {
    if (!focusSnippet) return;
    // Synchronous, no requestAnimationFrame: effects already run AFTER the DOM commit, so the
    // highlighted node exists — and rAF never fires in a background tab, which is exactly how the
    // click-through caught two "working" versions doing nothing. Direct assignment, direct now.
    const panel = document.querySelector('[data-testid="desk-source-doc"]');
    const el = panel && panel.querySelector('[data-hl="1"]');
    if (panel && el) panel.scrollTop = Math.max(0, el.offsetTop - panel.clientHeight / 2);
  }, [focusSnippet, docOpen]);

  const runSuggest = async (currentFields) => {
    setSuggesting(true);
    try {
      const intake = Object.fromEntries((currentFields || fields || []).map((f) => [f.key, f.value]));
      const r = await api.desk('suggest_checklist', { pack_key: pack.key, intake, corpora: laneSelections });
      setSuggestions(r.suggestions || []);
      const on = {}; (r.suggestions || []).forEach((x) => { on[x.item_key] = true; });
      setExtraOn(on);
      document.querySelector('[data-testid="desk-suggest"]')?.scrollIntoView({ block: 'center' });
    } catch (e) { toast.error(e.message); } finally { setSuggesting(false); }
  };

  const keepCheck = async (title, query_hint, origin) => {
    const r = await api.desk('add_check', { pack_key: pack.key, title, query_hint, origin });
    setUserChecks((prev) => (prev.some((c) => c.item_key === r.check.item_key) ? prev : [...prev, r.check]));
    setUserOn((prev) => ({ ...prev, [r.check.item_key]: true }));
    return r.check;
  };

  // "no preset" is a destination, not an empty slot: the pack's own checks, all on, and none of
  // yours - your rows stay in the library, they just are not part of this order.
  const clearPreset = () => {
    setCurrentPreset(null);
    setChecklistOff([]);
    setUserOn({});
    toast.success(t('desk.checks.presetCleared'));
  };

  // Load = restore the snapshot: the server turns referenced-but-removed rows back on and
  // returns the refreshed library, so a preset survives later deletions (Ali, 2026-08-24).
  const applyPreset = async (pr) => {
    try {
      const r = await api.desk('load_preset', { id: pr.id });
      setUserChecks(r.user_checks || []);
      setChecklistOff(r.preset.selection?.pack_off || []);
      const on = {};
      (r.user_checks || []).forEach((c) => { on[c.item_key] = (r.preset.selection?.user_on || []).includes(c.item_key); });
      setUserOn(on);
      setCurrentPreset(r.preset);
      toast.success(t('desk.checks.presetApplied', { name: r.preset.name }));
    } catch (e) { toast.error(e.message); }
  };

  const selectedUserChecks = userChecks.filter((c) => userOn[c.item_key] === true);
  const scanNames = corpora.filter((c) => c.on && c.dataset_ids.length).map((c) => c.name).join(' · ');
  const formulaSum = formula.reduce((a, l) => a + (Number(l.pct) || 0), 0);
  // phase1: attrs whose values the ✦ can propose as labelled ESTIMATES (pH/viscosity never)
  const ESTIMATABLE = ['kcal', 'fat_g', 'satfat_g', 'transfat_g', 'chol_mg', 'sodium_mg', 'carb_g',
    'fibre_g', 'sugars_g', 'added_sugars_g', 'protein_g', 'vitd_mcg', 'calcium_mg', 'iron_mg', 'potassium_mg'];
  const sameSet = (a, b) => a.length === b.length && [...a].sort().every((x, i) => x === [...b].sort()[i]);
  const presetDirty = !!currentPreset && !(
    sameSet(checklistOff, currentPreset.selection?.pack_off || [])
    && sameSet(selectedUserChecks.map((c) => c.item_key), currentPreset.selection?.user_on || [])
  );

  const gate = async (item_key, verb, patch) => {
    try {
      const r = await api.desk('gate_decision', { run_id: run.run_id, item_key, verb, patch });
      if (verb === 'park') { setRunParked(true); return; }
      if (verb === 'fix') {
        if (kind === 'review') {
          setRun((prev) => ({ ...prev, findings: prev.findings.map((f) => (f.item_key === item_key ? { ...f, verdict: 'fixed' } : f)) }));
          return;
        }
        // resume: rows that passed stay green and are NOT re-run; the rail animates only the rest
        const affected = new Set((run.findings || []).filter((f) => f.status !== 'ok').map((f) => f.item_key));
        setRerunning(affected);
        try {
          const rr = await api.desk('run_check', { order_id: orderId, resume: true });
          setRun(rr);
          if (rr.served_models?.length) setServedModel(rr.served_models.join(' · '));
        } finally { setRerunning(null); }
        return;
      }
      toast.success(t('desk.gate.accepted'));
      setRun((prev) => ({ ...prev, findings: prev.findings.map((f) => (f.item_key === item_key ? { ...f, verdict: 'accepted' } : f)) }));
      void r;
    } catch (e) {
      toast.error(e.message || t('desk.gate.blockRefused'));
    }
  };

  // PHASE 3 - the lab inside the build line: compose -> audit -> revise (server-side loop),
  // pre-flight refusal comes back with exactly which cells to fill, and the fill door fills them.
  const composeRun = async (alreadyFilled = false) => {
    const t0 = Date.now();
    setLabBusy(true); setLabCands(null); setLabPre(null); setLabResult(null);
    setLabStep('compose');
    try {
      const intake = Object.fromEntries((fields || []).map((f) => [f.key, f.value]));
      intake.serving_ml = Number(intake.serving_ml) || Number(String(intake.pack_format || '').match(/(\d+)\s*ml/i)?.[1]) || null;
      intake.claims = typeof intake.claims === 'string'
        ? intake.claims.split(/[·,;]/).map((x) => x.trim()).filter(Boolean)
        : (intake.claims || []);
      const r = await api.desk('suggest_formula', {
        pack_key: pack.key,
        goal: labGoal.trim() || String(intake.product_name || ''),
        creativity: labCreativity,
        // an existing formula is the lab's STARTING POINT - candidates are variations of it
        base_formula: formula
          .map((l) => ({ code: String(l.code).trim(), pct: Number(l.pct) }))
          .filter((l) => l.code && Number.isFinite(l.pct)),
        intake, corpora: laneSelections,
      });
      if (r.refused) {
        // one click means ONE click: fill the missing cells as estimates and retry, once
        if (!alreadyFilled) { await autoFillGaps(r.preflight); return; }
        setLabPre(r.preflight);
        return;
      }
      setLabPre(null);
      setLabHouse(r.house || null);
      const bestIdx = Number.isInteger(r.best) ? r.best : 0;
      const cand = (r.candidates || [])[bestIdx];
      if (cand) {
        // Ali's mock (2026-08-23): the suggestion appears in its OWN violet columns beside the
        // user's - values cross into the user's columns only through the use buttons.
        setLabResult({ name: cand.name, verdict: cand.verdict, counts: cand.counts, findings: cand.findings, solver: cand.solver, rationale: cand.rationale, lines: cand.lines });
        labLastSug.current = { name: cand.name, lines: cand.lines };
      }
      setLabCands((r.candidates || []).filter((_, i) => i !== bestIdx));
      setLabOpen(false);
      if (r.served_models?.length) setServedModel(r.served_models.join(' · '));
    } catch (e) { toast.error(e.message); } finally { setLabBusy(false); setLabStep(null); etaRecord('compose', Math.round((Date.now() - t0) / 1000)); }
  };

  const autoFillGaps = async (preflight) => {
    setLabFilling(true); setLabStep('fill');
    try {
      const byAttr = {};
      (preflight?.excluded || []).forEach((x) => x.missing.forEach((a) => {
        if (a !== 'components') { (byAttr[a] = byAttr[a] || []).push(x.code); }
      }));
      // N-128b: the estimator may honestly return nothing for a cell. Silence here looped the
      // user back into the same refusal - now every unfilled cell is NAMED.
      setLabFillReport(null);
      let filled = 0;
      const unfilled = [];
      // speed (Ali: "takes a long time"): the cells are independent - every attr call and every
      // patch runs in parallel instead of one long chain.
      await Promise.all(Object.entries(byAttr).map(async ([attr, codes]) => {
        const r2 = await api.desk('suggest_attr_values', { attr, codes });
        const usable = (r2.suggestions || []).filter((sug) => String(sug.value).trim());
        await Promise.all(usable.map((sug) => api.desk('patch_material', { code: sug.code, attrs: { [attr]: sug.value }, modeled: !!r2.modeled })));
        filled += usable.length;
        const got = new Set(usable.map((x) => x.code));
        codes.filter((c) => !got.has(c)).forEach((c) => unfilled.push({ code: c, attr }));
      }));
      setLabFillReport({ filled, unfilled });
      await composeRun(true);
    } catch (e) { toast.error(e.message); } finally { setLabFilling(false); }
  };

  const fillGapsAndRetry = async () => { await autoFillGaps(labPre); };

  const openPreview = async () => {
    try {
      const r = await api.desk('preview_run', { run_id: run.run_id });
      setPreviewDoc(r.artifact);
    } catch (e) {
      toast.error(e.message || t('desk.gate.blockRefused'));
      if (e.status === 409) document.querySelector('[data-testid="desk-rail"]')?.scrollIntoView({ block: 'start', behavior: 'smooth' });
    }
  };

  const sign = async () => {
    try {
      const r = await api.desk('sign_run', { run_id: run.run_id });
      setSigned(r.artifact);
      setBenchDoc(r.artifact);
      api.desk('list_orders').then((x) => setOrders(x.orders || [])).catch(() => {});
      setView('bench');
    } catch (e) {
      toast.error(e.message || t('desk.gate.signRefused'));
      if (e.status === 409) document.querySelector('[data-testid="desk-rail"]')?.scrollIntoView({ block: 'start', behavior: 'smooth' });
    }
  };

  // N-129: the learning switch, finally with a handle. Flips server-side and updates the shelf.
  const toggleLearning = async (order_id, learn) => {
    try {
      await api.desk('set_learning', { order_id, learn });
      setOrders((prev) => prev.map((o) => (o.id === order_id ? { ...o, no_learn: !learn } : o)));
      toast.success(t(learn ? 'desk.lab.teacherIncluded' : 'desk.lab.teacherExcluded'));
    } catch (e) { toast.error(e.message); }
  };

  const openOrderFromBench = useCallback((prefill, parent, note) => {
    setKind('build'); setSuggestions(null); setExtraOn({}); setExtras([]); setObservations([]);
    setFields(schemaFields.map((f) => ({ key: f.key, value: prefill?.[f.key] ?? null, confidence: prefill ? 100 : 0, source_snippet: null })));
    setFormula((prefill?.formula || [{ code: '', pct: '' }]).map((l) => ({ code: l.code, pct: l.pct })));
    setOrderId(null); setRun(null); setSigned(null); setIntakeMode('hand');
    setParentOrderId(parent || null); setOrderNote(note || null);
    setView('order');
  }, [schemaFields]);

  /* ─────────────── home ─────────────── */
  if (view === 'home') {
    return (
      <div className="mx-auto max-w-4xl p-6" data-testid="desk-home">
        <Assistant>{t('desk.home.greeting')}</Assistant>
        <div className="mb-5 grid gap-4 md:grid-cols-2">
          <button
            type="button"
            className="rounded-2xl border-2 border-border bg-card p-6 text-start transition hover:-translate-y-0.5 hover:border-primary hover:shadow-lg"
            onClick={() => { setKind('build'); setSuggestions(null); setExtraOn({}); setExtras([]); setObservations([]); setIntakeMode(null); setFields(null); setOrderId(null); setParentOrderId(null); setOrderNote(null); setLabCands(null); setLabPre(null); setImgReadings(null); setView('order'); }}
            data-testid="desk-door-build"
          >
            <Hammer className="mb-2 h-7 w-7 text-primary" />
            <div className="text-base font-bold">{t('desk.home.buildTitle')}</div>
            <p className="mt-1 text-xs leading-6 text-muted-foreground">{t('desk.home.buildDesc')}</p>
          </button>
          <button
            type="button"
            className="rounded-2xl border-2 border-border bg-card p-6 text-start transition hover:-translate-y-0.5 hover:border-primary hover:shadow-lg"
            onClick={() => { setKind('review'); setSuggestions(null); setExtraOn({}); setExtras([]); setObservations([]); setIntakeMode(null); setFields(null); setDocText(''); setOrderId(null); setRun(null); setSigned(null); setParentOrderId(null); setOrderNote(null); setLabCands(null); setLabPre(null); setImgReadings(null); setView('order'); }}
            data-testid="desk-door-review"
          >
            <SearchCheck className="mb-2 h-7 w-7 text-primary" />
            <div className="text-base font-bold">{t('desk.home.reviewTitle')}</div>
            <p className="mt-1 text-xs leading-6 text-muted-foreground">{t('desk.home.reviewDesc')}</p>
          </button>
        </div>

        {/* Two config boxes (Ali, 2026-08-24): what the desk READS FROM and what it TESTS
            AGAINST — same-nature things share one box. */}
        <div className="grid items-start gap-4 md:grid-cols-2">
          <div className="rounded-2xl border border-border bg-card p-4" data-testid="desk-sources-box">
            <h3 className="mb-2 text-sm font-bold">{t('desk.sources.boxTitle')}</h3>
            <h4 className="mb-1 text-xs font-bold">{t('desk.sources.corpora')}</h4>
            <p className="mb-2 text-xs text-muted-foreground">{t('desk.sources.corporaHint')}</p>
            {corpora.map((c, i) => (
              <label key={i} className="mb-1.5 flex cursor-pointer items-center gap-2 rounded-lg border border-border px-3 py-2 text-xs">
                <input type="checkbox" checked={c.on} onChange={(e) => setCorpora((p) => p.map((x, j) => (j === i ? { ...x, on: e.target.checked } : x)))} />
                <span className="flex-1">{c.name}</span>
                <span className={`rounded-full px-2 py-0.5 text-[10px] ${c.label === 'law' ? 'bg-primary/10 text-primary' : 'bg-success-soft text-success'}`}>
                  {t(c.label === 'law' ? 'desk.sources.laneLaw' : 'desk.sources.laneOwn')}
                </span>
              </label>
            ))}
            <Button size="sm" variant="outline" onClick={() => { window.location.href = '/admin/corpus'; }} data-testid="desk-goto-corpora">
              {t('desk.sources.buildCorpus')}
            </Button>
            <div className="mt-4 border-t border-border pt-3">
            <h4 className="mb-1 text-xs font-bold">{t('desk.sources.materials')}</h4>
            <p className="mb-2 text-xs text-muted-foreground">{t('desk.sources.materialsHint')}</p>
            {materials ? (
              <p className="mb-2 text-xs">
                <FileCheck2 className="me-1 inline h-3.5 w-3.5 text-success" />
                {t('desk.sources.materialsState', materials)}
              </p>
            ) : (
              <p className="mb-2 text-xs text-warning">{t('desk.sources.materialsEmpty')}</p>
            )}
            <input ref={matsRef} type="file" accept=".xlsx,.xls" className="hidden" onChange={(e) => e.target.files[0] && importMaterials(e.target.files[0])} />
            <Button size="sm" onClick={() => matsRef.current?.click()} data-testid="desk-import-materials">
              <Upload className="me-1.5 h-3.5 w-3.5" />{t('desk.sources.upload')}
            </Button>
            </div>
          </div>
          <div className="rounded-2xl border border-border bg-card p-4" data-testid="desk-checks-box">
            <div className="mb-1 flex items-center gap-2">
              <h3 className="flex-1 text-sm font-bold">{t('desk.checks.title')}</h3>
              <button
                type="button" className="text-[11px] text-primary underline" data-testid="desk-checks-manage"
                onClick={() => setManageOpen(true)}
              >
                {t('desk.checks.countLine', { checks: (pack?.checklist || []).length + userChecks.length, presets: presets.length })}
              </button>
            </div>
            <p className="mb-2 text-xs text-muted-foreground">{t('desk.checks.hint')}</p>
            <p className="mb-2 text-[10px]" data-testid="desk-curation">
              {pack?.curated_by
                ? <span className="text-success">{t('desk.checks.curatedBy', { name: pack.curated_by, date: pack.curated_at || '' })}</span>
                : <span className="text-warning">{t('desk.checks.notCurated')}</span>}
            </p>
            {presets.length > 0 && (
              <div className="mb-2 flex items-center gap-2">
                <span className="flex-none text-[11px] text-muted-foreground">{t('desk.checks.presetLabel')}</span>
                <select
                  className="h-8 flex-1 rounded-lg border border-border bg-background px-2 text-xs"
                  value={currentPreset?.id ?? ''} data-testid="desk-preset-select"
                  onChange={(e) => {
                    const pr = presets.find((x) => x.id === e.target.value);
                    if (pr) applyPreset(pr); else clearPreset();
                  }}
                >
                  <option value="">{t('desk.checks.presetNone')}</option>
                  {presets.map((pr) => (
                    <option key={pr.id} value={pr.id}>
                      {pr.id === currentPreset?.id && presetDirty
                        ? t('desk.checks.presetCurrentEdited', { name: pr.name })
                        : pr.name}
                    </option>
                  ))}
                </select>
              </div>
            )}
            {(pack?.checklist || []).map((c) => (
              <label key={c.item_key} className="mb-1 flex cursor-pointer items-center gap-2 text-xs">
                <input
                  type="checkbox"
                  checked={!checklistOff.includes(c.item_key)}
                  onChange={(e) => setChecklistOff((p) => (e.target.checked ? p.filter((k) => k !== c.item_key) : [...p, c.item_key]))}
                />
                <span className="flex-1">{c.title}</span>
                <span className="rounded-full bg-muted px-2 py-0.5 text-[10px] text-muted-foreground">{t('desk.checks.originPack')}</span>
              </label>
            ))}
            {selectedUserChecks.map((c) => (
              <label key={c.item_key} className="mb-1 flex cursor-pointer items-center gap-2 text-xs">
                <input
                  type="checkbox"
                  checked
                  onChange={() => setUserOn((prev) => ({ ...prev, [c.item_key]: false }))}
                />
                <span className="flex-1">{c.title}</span>
                <span className={`rounded-full px-2 py-0.5 text-[10px] ${c.origin === 'user' ? 'bg-success-soft text-success' : 'bg-violet-500/10 text-violet-600'}`}>
                  {t(`desk.checks.origin${c.origin === 'user' ? 'User' : c.origin === 'corpus' ? 'Corpus' : 'Ai'}`)}
                </span>
                <button
                  type="button" className="px-1 text-muted-foreground hover:text-destructive" title={t('desk.checks.remove')}
                  onClick={(e) => {
                    e.preventDefault();
                    api.desk('remove_check', { id: c.id }).then(() => {
                      setUserChecks((prev) => prev.filter((x) => x.id !== c.id));
                    }).catch((err) => toast.error(err.message));
                  }}
                >×</button>
              </label>
            ))}
            {userChecks.length > selectedUserChecks.length && (
              <button
                type="button" className="mt-1 text-[11px] text-primary underline" data-testid="desk-checks-outside"
                onClick={() => setManageOpen(true)}
              >
                {t('desk.checks.notInOrder', { n: userChecks.length - selectedUserChecks.length })}
              </button>
            )}
            <div className="mt-2 flex items-center gap-2">
              <Input
                className="h-8 flex-1 text-xs" placeholder={t('desk.checks.addPh')} value={addCheckText}
                onChange={(e) => setAddCheckText(e.target.value)} data-testid="desk-check-add"
                onKeyDown={(e) => { if (e.key === 'Enter' && addCheckText.trim()) { keepCheck(addCheckText.trim(), addCheckText.trim(), 'user').then(() => setAddCheckText('')).catch((err) => toast.error(err.message)); } }}
              />
              <Button size="sm" variant="outline" disabled={!addCheckText.trim() || !pack}
                onClick={() => keepCheck(addCheckText.trim(), addCheckText.trim(), 'user').then(() => setAddCheckText('')).catch((err) => toast.error(err.message))}
              >
                {t('desk.checks.addBtn')}
              </Button>
            </div>
            <div className="mt-2">
              <Button
                size="sm" variant="outline" disabled={homeSuggesting || !pack || !laneSelections.length} data-testid="desk-check-suggest"
                onClick={async () => {
                  setHomeSuggesting(true);
                  try {
                    const r = await api.desk('suggest_checklist', { pack_key: pack.key, intake: {}, corpora: laneSelections });
                    setHomeSugs(r.suggestions || []);
                  } catch (e) { toast.error(e.message); } finally { setHomeSuggesting(false); }
                }}
              >
                ✦ {homeSuggesting ? t('desk.checks.aiSuggesting') : t('desk.checks.aiSuggest')}
              </Button>
              <p className="mt-1 text-[10px] text-muted-foreground">
                {laneSelections.length ? t('desk.checks.scanScope', { names: scanNames }) : t('desk.checks.scanNoCorpora')}
              </p>
              {homeSuggesting && (
                <div className="mt-2 rounded-xl border border-primary/40 bg-card p-3" data-testid="desk-scan-progress">
                  <div className="flex items-center gap-2">
                    <span className="theater-dot" aria-hidden="true" />
                    <p className="text-xs font-medium text-primary">{t('desk.checks.scanningNames', { names: scanNames })}</p>
                  </div>
                  <div className="mt-1 flex gap-4 text-[11px]">
                    <span>{t('theater.elapsed')} <b className="font-data tabular-nums">0:{String(homeSugSec).padStart(2, '0')}</b></span>
                    <span className="text-muted-foreground">{t('desk.checks.scanTypical')}</span>
                  </div>
                  <span className="desk-shimmer mt-2 block h-5 rounded-lg" />
                </div>
              )}
              {homeSugs && homeSugs.length === 0 && (
                <p className="mt-1 text-[11px] text-muted-foreground">{t('desk.checks.aiNone')}</p>
              )}
              {(homeSugs || []).map((x) => (
                <div key={x.item_key} className="mt-2 flex items-start gap-2 rounded-lg border border-violet-300/50 bg-violet-500/5 p-2 text-xs">
                  <span className="flex-1"><b>{x.title}</b>
                    <span className="block text-[11px] text-muted-foreground">{x.why}</span>
                    <span className="block text-[10px] text-violet-600">{t('desk.run.citation')}: {x.source}</span></span>
                  <Button size="sm" variant="outline" onClick={() => {
                    keepCheck(x.title, x.query_hint, 'corpus')
                      .then(() => setHomeSugs((prev) => prev.filter((y) => y.item_key !== x.item_key)))
                      .catch((err) => toast.error(err.message));
                  }}>
                    {t('desk.checks.keep')}
                  </Button>
                </div>
              ))}
            </div>
            <div className="mt-3 flex items-center gap-2 border-t border-border pt-2">
              <Input
                className="h-8 flex-1 text-xs" placeholder={t('desk.checks.presetNamePh')} value={presetName}
                onChange={(e) => setPresetName(e.target.value)} data-testid="desk-preset-name"
              />
              <Button size="sm" variant="outline" disabled={!presetName.trim()} data-testid="desk-preset-save"
                onClick={async () => {
                  try {
                    const r = await api.desk('save_preset', {
                      pack_key: pack.key, name: presetName.trim(),
                      selection: { pack_off: checklistOff, user_on: selectedUserChecks.map((c) => c.item_key) },
                    });
                    setPresets((prev) => [...prev.filter((x) => x.id !== r.preset.id), r.preset]);
                    setCurrentPreset(r.preset);
                    setPresetName('');
                    toast.success(t('desk.checks.presetSaved', { name: r.preset.name }));
                  } catch (e) { toast.error(e.message); }
                }}
              >
                {t('desk.checks.savePreset')}
              </Button>
            </div>
          </div>
        </div>
        {orders.some((o) => o.run?.status === 'signed') && (
          <div className="mt-4">
            <Button variant="outline" onClick={() => setView('bench')} data-testid="desk-goto-bench">
              {t('desk.home.gotoBench', { n: orders.filter((o) => o.run?.status === 'signed').length })}
            </Button>
          </div>
        )}
        {manageOpen && (
          <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
            data-testid="desk-manage-popup"
            onClick={() => setManageOpen(false)}
            onKeyDown={(e) => { if (e.key === 'Escape') setManageOpen(false); }}
            role="presentation"
          >
            <div
              className="max-h-[80vh] w-full max-w-lg overflow-y-auto rounded-2xl border border-border bg-card p-4"
              onClick={(e) => e.stopPropagation()}
              role="dialog" aria-modal="true"
            >
              <div className="mb-2 flex items-center gap-2">
                <h3 className="flex-1 text-sm font-bold">{t('desk.checks.manageTitle')}</h3>
                <Button size="sm" variant="ghost" onClick={() => setManageOpen(false)}>{t('desk.gate.cancel')}</Button>
              </div>
              <h4 className="text-xs font-bold">{t('desk.checks.manageChecks', { n: (pack?.checklist || []).length + userChecks.length })}</h4>
              <p className="mb-1 text-[11px] text-muted-foreground">{t('desk.checks.manageHint')}</p>
              {(pack?.checklist || []).map((c) => (
                <div key={c.item_key} className="flex items-center gap-2 border-b border-muted py-1.5 text-xs last:border-0">
                  <span className="flex-1">{c.title}</span>
                  <span className="rounded-full bg-muted px-2 py-0.5 text-[10px] text-muted-foreground">{t('desk.checks.originPack')}</span>
                </div>
              ))}
              {userChecks.map((c) => (
                <div key={c.item_key} className="flex items-center gap-2 border-b border-muted py-1.5 text-xs last:border-0">
                  <input
                    type="checkbox" title={t('desk.checks.inThisOrder')}
                    checked={userOn[c.item_key] === true}
                    onChange={(e) => setUserOn((prev) => ({ ...prev, [c.item_key]: e.target.checked }))}
                  />
                  <span className="flex-1">{c.title}</span>
                  <span className={`rounded-full px-2 py-0.5 text-[10px] ${c.origin === 'user' ? 'bg-success-soft text-success' : 'bg-violet-500/10 text-violet-600'}`}>
                    {t(`desk.checks.origin${c.origin === 'user' ? 'User' : c.origin === 'corpus' ? 'Corpus' : 'Ai'}`)}
                  </span>
                  <button
                    type="button" className="px-1 text-muted-foreground hover:text-destructive" title={t('desk.checks.remove')}
                    onClick={() => {
                      api.desk('remove_check', { id: c.id }).then(() => {
                        setUserChecks((prev) => prev.filter((x) => x.id !== c.id));
                      }).catch((err) => toast.error(err.message));
                    }}
                  >×</button>
                </div>
              ))}
              <h4 className="mb-1 mt-3 border-t border-border pt-3 text-xs font-bold">{t('desk.checks.managePresets', { n: presets.length })}</h4>
              {presets.length === 0 && <p className="text-[11px] text-muted-foreground">{t('desk.checks.noPresets')}</p>}
              {presets.map((pr) => {
                const on = (pack?.checklist || []).length - (pr.selection?.pack_off || []).length + (pr.selection?.user_on || []).length;
                return (
                  <div key={pr.id} className="flex items-center gap-2 border-b border-muted py-1.5 text-xs last:border-0">
                    <span className="flex-1"><b>{pr.name}</b>
                      {currentPreset?.id === pr.id && (
                        <span className="ms-1 rounded-full bg-primary/10 px-2 py-0.5 text-[10px] text-primary">
                          {presetDirty ? t('desk.checks.presetCurrentTagEdited') : t('desk.checks.presetCurrentTag')}
                        </span>
                      )}
                      <span className="ms-2 text-[10px] text-muted-foreground">{t('desk.checks.presetSummary', { n: on })}</span></span>
                    <Button size="sm" variant="outline" onClick={() => { applyPreset(pr); setManageOpen(false); }}>{t('desk.checks.presetLoad')}</Button>
                    <button
                      type="button" className="px-1 text-muted-foreground hover:text-destructive" title={t('desk.checks.presetDelete')}
                      onClick={() => {
                        api.desk('delete_preset', { id: pr.id }).then(() => {
                          setPresets((prev) => prev.filter((x) => x.id !== pr.id));
                          setCurrentPreset((cur) => (cur?.id === pr.id ? null : cur));
                          toast.success(t('desk.checks.presetDeleted', { name: pr.name }));
                        }).catch((err) => toast.error(err.message));
                      }}
                    >×</button>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    );
  }

  /* ─────────────── order (intake) ─────────────── */
  if (view === 'order') {
    return (
      <div className="mx-auto max-w-5xl p-6" data-testid="desk-order">
        <Button variant="ghost" size="sm" className="mb-2" onClick={() => setView('home')}>
          <ArrowLeft className="me-1 h-4 w-4 rtl:rotate-180" />{t('desk.back')}
        </Button>
        {orderNote && (
          <div className="mb-3 rounded-xl border border-warning-border bg-warning-soft p-3 text-xs" data-testid="desk-order-note">
            <b>{orderNote.kind === 'fix' ? t('desk.bench.fixBanner') : t('desk.bench.nextBanner')}</b>
            <p className="mt-0.5 text-muted-foreground">{orderNote.text}</p>
          </div>
        )}
        {!fields && (
          <>
            <Assistant>{t(kind === 'review' ? 'desk.review.greeting' : 'desk.order.greeting')}</Assistant>
            <div className={`grid gap-3 ${kind === 'review' ? 'md:grid-cols-2' : 'md:grid-cols-3'}`}>
              <button type="button" className="rounded-2xl border-2 border-dashed border-border bg-card p-6 text-center text-sm hover:border-primary" onClick={() => fileRef.current?.click()} data-testid="desk-intake-upload">
                <Upload className="mx-auto mb-2 h-6 w-6" /><b>{t('desk.order.doorUpload')}</b>
                <p className="mt-1 text-xs text-muted-foreground">{t('desk.order.doorUploadHint')}</p>
              </button>
              <button type="button" className="rounded-2xl border-2 border-dashed border-border bg-card p-6 text-center text-sm hover:border-primary" onClick={() => setIntakeMode('paste')} data-testid="desk-intake-paste">
                <ClipboardPaste className="mx-auto mb-2 h-6 w-6" /><b>{t('desk.order.doorPaste')}</b>
              </button>
              {kind !== 'review' && (
              <button
                type="button" className="rounded-2xl border-2 border-dashed border-border bg-card p-6 text-center text-sm hover:border-primary"
                onClick={() => setFields(allFieldDefs.map((f) => ({ key: f.key, value: null, confidence: 0, source_snippet: null, extracted: null })))}
                data-testid="desk-intake-hand"
              >
                <Keyboard className="mx-auto mb-2 h-6 w-6" /><b>{t('desk.order.doorHand')}</b>
              </button>
              )}
            </div>
            <input
              ref={fileRef} type="file" accept={ACCEPT_DOCS} className="hidden"
              onChange={async (e) => {
                const f = e.target.files[0]; if (!f) return;
                e.target.value = '';
                try {
                  const { text, images } = await extractFileText(f);
                  setDocText(text); extract(text, images);
                } catch (err) {
                  // the honest hard-block: no readable text -> no invented intake
                  toast.error(t(`desk.file.${err.message}`, { defaultValue: t('desk.file.file-unsupported') }));
                }
              }}
            />
            {intakeMode === 'paste' && (
              <div className="mt-3">
                <textarea
                  className="h-40 w-full rounded-xl border border-border bg-background p-3 text-sm"
                  placeholder={t('desk.order.pastePh')} value={docText} onChange={(e) => setDocText(e.target.value)}
                />
                <Button className="mt-2" disabled={!docText.trim() || extracting} onClick={() => extract(docText)}>
                  {t('desk.order.extract')}
                </Button>
              </div>
            )}
            {extracting && (
              <div className="mt-3" data-testid="desk-extract-skeleton">
                <div className="mb-3 rounded-xl border border-primary/40 bg-card p-3">
                  <div className="flex items-center gap-2">
                    <span className="theater-dot" aria-hidden="true" />
                    <p className="text-sm font-medium text-primary">{t('desk.order.skeletonTitle')}</p>
                  </div>
                  <div className="mt-2 flex gap-4 text-xs">
                    <span>{t('theater.elapsed')} <b className="font-data tabular-nums">0:{String(extractSec).padStart(2, '0')}</b></span>
                    <span className="text-muted-foreground">{etaMedian('extract') ? t('desk.order.typicalMeasured', { s: etaMedian('extract') }) : t('desk.order.skeletonTypical')}</span>
                  </div>
                </div>
                {(schemaFields || []).map((f) => (
                  <div key={f.key} className="mb-2 grid grid-cols-[110px_1fr_auto] items-center gap-2">
                    <span className="text-xs text-muted-foreground">{f.label}</span>
                    <span className="desk-shimmer h-8 rounded-lg" />
                    <span className="desk-shimmer h-5 w-16 rounded-md" />
                  </div>
                ))}
              </div>
            )}
          </>
        )}
        {fields && intakeWarnings.length > 0 && (
          <div className="mb-3 rounded-xl border-2 border-destructive/60 bg-destructive/5 p-3 text-xs" data-testid="desk-intake-warnings">
            <b className="mb-1 block text-destructive">{t('desk.order.warnTitle')}</b>
            <ul className="ms-4 list-disc space-y-1">
              {intakeWarnings.map((w, i) => (
                <li key={i}>
                  {w.code === 'formula_sum' ? t('desk.order.warnFormulaSum', { sum: w.sum, lines: w.lines })
                    : w.code === 'extras_cap' ? t('desk.order.warnExtrasCap', { kept: w.kept })
                      : t('desk.order.warnTruncated')}
                </li>
              ))}
            </ul>
            <p className="mt-1 text-[11px] text-muted-foreground">{t('desk.order.warnHint')}</p>
          </div>
        )}
        {imgReading && (
          <div className="mb-3 rounded-xl border border-primary/40 bg-card p-3" data-testid="desk-img-reading">
            <div className="flex items-center gap-2"><span className="theater-dot" aria-hidden="true" />
              <p className="text-sm font-medium text-primary">{t('desk.order.imgReading')}</p></div>
          </div>
        )}
        {fields && (imgReadings || []).length > 0 && (
          <div className="mb-3 rounded-xl border border-border bg-card p-3 text-xs" data-testid="desk-img-readings">
            <div className="mb-1 flex items-center gap-2">
              <b className="flex-1">{t('desk.order.imgTitle')}</b>
              <span className="rounded-full bg-violet-500/10 px-2 py-0.5 text-[10px] text-violet-600">{t('desk.order.imgChip')}</span>
            </div>
            {imgReadings.filter((rd) => rd.kind === 'unreadable').map((rd, ri) => (
              <p key={`u${ri}`} className="rounded-lg bg-warning-soft p-2 text-[11px] text-warning" data-testid="desk-img-unreadable">
                {t('desk.order.imgUnreadable')} <span className="text-muted-foreground">({rd.description.slice(0, 140)})</span>
              </p>
            ))}
            {imgReadings.map((rd, ri) => rd.kind === 'nutrition_table' ? (
              <table key={ri} className="mt-1 w-full text-[11px]">
                <tbody>
                  <tr className="text-muted-foreground"><th className="text-start">{t('desk.review.colNutrient')}</th><th>per 100</th><th>{t('desk.review.colPerServing')}</th></tr>
                  {rd.rows.map((row, i2) => (
                    <tr key={i2} className="border-t border-muted">
                      <td>{row.label}</td>
                      <td className={/\?/.test(row.per_100) ? 'text-warning' : ''} dir="ltr">{row.per_100} {row.unit}</td>
                      <td className={/\?/.test(row.per_serving) ? 'text-warning' : ''} dir="ltr">{row.per_serving} {row.unit}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <p key={ri} className="mt-1 text-muted-foreground">{t('desk.order.imgOther', { desc: rd.description })}</p>
            ))}
            {imgReadings.some((rd) => rd.rows?.some((row) => /\?/.test(row.per_100 + row.per_serving))) && (
              <p className="mt-1 text-[10px] text-warning">{t('desk.order.imgUnsure')}</p>
            )}
          </div>
        )}
        {fields && docKind === 'out_of_domain' && (
          <div className="mb-3 rounded-xl border border-destructive/50 bg-destructive/5 p-3 text-xs" data-testid="desk-ood-panel">
            <b className="text-destructive">{t('desk.order.oodTitle')}</b>
            <p className="mt-0.5 text-muted-foreground">{docKindNote}</p>
            <p className="mt-1 text-muted-foreground">{t('desk.order.oodBody')}</p>
          </div>
        )}
        {fields && docKind === 'ingredient_spec' && (
          <div className="mb-3 rounded-xl border border-primary/40 bg-primary/5 p-3 text-xs" data-testid="desk-ingspec-note">
            <b className="text-primary">{t(kind === 'review' ? 'desk.order.ingSpecReview' : 'desk.order.ingSpecBuild')}</b>
            {docKindNote && <p className="mt-0.5 text-muted-foreground">{docKindNote}</p>}
          </div>
        )}
        {fields && observations.length > 0 && (
          <div className="mb-3 rounded-xl border border-warning-border bg-warning-soft p-3 text-xs" data-testid="desk-observations">
            <b className="mb-1 block">{t('desk.order.obsTitle')}</b>
            <ul className="ms-4 list-disc space-y-1">
              {observations.map((o, i) => <li key={i}>{o}</li>)}
            </ul>
            <p className="mt-1 text-[10px] text-muted-foreground">{t('desk.order.obsHint')}</p>
          </div>
        )}
        {fields && (
          <>
            <div className="mb-4 rounded-2xl border border-border bg-card px-4 py-3 text-xs" data-testid="desk-source-strip">
              <button type="button" className="flex w-full items-center gap-2" onClick={() => setDocOpen((v) => !v)}>
                <b className="flex-none text-muted-foreground">{t('desk.order.sourceDoc')}</b>
                <span className="flex-1 truncate text-start text-[11px] text-muted-foreground">
                  {t('desk.order.extracted', {
                    found: fields.filter((f) => f.value != null).length,
                    missing: fields.filter((f) => f.value == null).length,
                  })}
                </span>
                <span className="flex-none text-muted-foreground">{docOpen ? '▴' : '▾'}</span>
              </button>
              {!docOpen && (
                (() => {
                  const focusLine = focusSnippet
                    ? (docText.split('\n').find((l) => l.includes(focusSnippet)) || focusSnippet)
                    : null;
                  const peek = docText.split('\n').filter((l) => l.trim()).slice(0, 2).join(' · ');
                  return (
                    <p
                      className={`mt-1 max-h-10 overflow-hidden leading-5 ${focusLine ? 'rounded bg-warning-soft px-1 outline outline-1 outline-warning' : 'text-muted-foreground'}`}
                      data-testid="desk-source-peek"
                    >
                      {focusLine || peek || t('desk.order.noDoc')}
                    </p>
                  );
                })()
              )}
              {docOpen && (
                <div className="mt-2 max-h-[50vh] overflow-y-auto border-t border-border pt-2 leading-7" data-testid="desk-source-doc">
                  {docText
                    ? docText.split('\n').map((line, i) => (
                      <p key={i} data-hl={focusSnippet && line.includes(focusSnippet) ? '1' : undefined} className={focusSnippet && line.includes(focusSnippet) ? 'rounded bg-warning-soft outline outline-1 outline-warning' : ''}>
                        {line}
                      </p>
                    ))
                    : <p className="text-muted-foreground">{t('desk.order.noDoc')}</p>}
                  {focusSnippet && !docText.includes(focusSnippet) && (
                    <p className="mt-2 rounded bg-warning-soft p-2">{t('desk.order.snippet')}: {focusSnippet}</p>
                  )}
                </div>
              )}
            </div>
            <div className="grid items-start gap-4 md:grid-cols-2">
              <div className="rounded-2xl border border-border bg-card p-4" data-testid="desk-intake-form">
                <p className="mb-2 text-[11px] text-muted-foreground">
                {t('desk.order.confLegend')}
                {servedModel && <span className="ms-2 rounded-md bg-muted px-1.5 py-0.5 font-data text-[10px]" data-testid="desk-model-chip" dir="ltr">{servedModel}</span>}
                {docKind && <span className="ms-2 rounded-md bg-primary/10 px-1.5 py-0.5 text-[10px] text-primary" data-testid="desk-dockind-chip" title={docKindNote || ''}>{t(`desk.order.kind_${docKind}`)}</span>}
              </p>
                {fields.map((f, i) => {
                  const def = allFieldDefs.find((s) => s.key === f.key) || (f.label ? { label: f.label } : null);
                  const edited = f.extracted != null && f.value !== f.extracted;
                  return (
                    <div key={f.key} className="grid grid-cols-[110px_1fr_auto] items-center gap-2 border-b border-muted py-1.5 text-xs">
                      <span className="text-muted-foreground">
                        {def?.label || f.key}{def?.required ? ' *' : ''}
                        {edited && (
                          <button
                            type="button" className="mt-0.5 block text-start text-[10px] text-warning underline"
                            title={t('desk.order.provRestore')}
                            onClick={() => setFields((prev) => prev.map((x, j) => (j === i ? { ...x, value: x.extracted } : x)))}
                          >
                            {t('desk.order.provWas', { value: String(f.extracted).slice(0, 24) })}
                          </button>
                        )}
                      </span>
                      <Input
                        className="h-8 text-xs" value={f.value ?? ''} placeholder="—"
                        onChange={(e) => setFields((p) => p.map((x, j) => (j === i ? { ...x, value: e.target.value || null } : x)))}
                        onFocus={() => setFocusSnippet(f.source_snippet)}
                      />
                      {f.value == null
                        ? <span className="rounded-md bg-warning-soft px-2 py-0.5 text-[10px] text-warning">{t('desk.order.notInDoc')}</span>
                        : (
                          <button
                            type="button" onClick={() => setFocusSnippet(f.source_snippet)}
                            className={`rounded-md px-2 py-0.5 text-[10px] ${f.confidence < 80 ? 'bg-warning-soft text-warning' : 'bg-muted text-muted-foreground'}`}
                            title={t('desk.order.confTitle')}
                          >
                            {f.source_snippet ? t('desk.order.fromDoc', { pct: f.confidence }) : `${f.confidence}%`}
                          </button>
                        )}
                    </div>
                  );
                })}
                {extras.length > 0 && (
                  <div className="mt-3 rounded-xl border border-border bg-muted/40 p-3" data-testid="desk-extras">
                    <div className="flex items-center gap-2">
                      <h3 className="flex-1 text-xs font-bold">{t('desk.order.extrasTitle')}</h3>
                      <label className="flex cursor-pointer items-center gap-1 text-[11px] text-muted-foreground">
                        <input
                          type="checkbox" data-testid="desk-extras-all"
                          checked={extras.length > 0 && extras.every((x) => x.on)}
                          onChange={(e) => setExtras(extras.map((y) => ({ ...y, on: e.target.checked })))}
                        />
                        {t('desk.order.selectAll')}
                      </label>
                    </div>
                    <p className="mb-2 text-[11px] text-muted-foreground">{t('desk.order.extrasHint')}</p>
                    {extras.map((x, i) => (
                      <div key={i} className={`flex flex-wrap items-center gap-2 border-b border-muted py-1.5 text-xs last:border-0 ${x.on ? '' : 'opacity-45'}`}>
                        <input type="checkbox" checked={x.on} onChange={(e) => setExtras(extras.map((y, j) => (j === i ? { ...y, on: e.target.checked } : y)))} />
                        <span className="w-36 flex-none truncate text-muted-foreground" title={x.source_snippet || ''}>{x.label}</span>
                        <input
                          className="flex-1 rounded-lg border border-border bg-background px-2 py-1"
                          value={x.value}
                          onChange={(e) => setExtras(extras.map((y, j) => (j === i ? { ...y, value: e.target.value } : y)))}
                        />
                        <Button
                          size="sm" variant="outline" className="h-7 flex-none px-2 text-[11px]"
                          title={t('desk.order.promoteHint')}
                          onClick={async () => {
                            try {
                              const r = await api.desk('add_field', { pack_key: pack.key, label: x.label });
                              setUserFields((prev) => (prev.some((uf) => uf.key === r.field.key) ? prev : [...prev, r.field]));
                              setFields((prev) => (prev.some((ff) => ff.key === r.field.key) ? prev
                                : [...prev, { key: r.field.key, label: r.field.label, value: x.value, confidence: 100, source_snippet: x.source_snippet || null, extracted: x.value, custom: true }]));
                              setExtras((prev) => prev.filter((_, j) => j !== i));
                              toast.success(t('desk.order.promoted', { label: x.label }));
                            } catch (e) { toast.error(e.message); }
                          }}
                        >
                          ↑ {t('desk.order.promote')}
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <div className="sticky top-20 max-h-[80vh] overflow-y-auto rounded-2xl border border-border bg-card p-4" data-testid="desk-build-panel">
                <div>
                  <p className="mb-1 text-xs font-bold">{t('desk.order.formula')}</p>
                  {labResult && (
                    <div className={`mb-2 rounded-xl border p-2 text-[11px] ${labResult.verdict === 'passed' ? 'border-success/50 bg-success-soft/40' : labResult.verdict === 'conditional' ? 'border-warning-border bg-warning-soft/40' : 'border-destructive/50 bg-destructive/5'}`} data-testid="desk-lab-result">
                      <div className="flex items-center gap-2">
                        <span className="flex-1">
                          {labResult.verdict === 'passed' && t('desk.lab.badgePassed')}
                          {labResult.verdict === 'conditional' && t('desk.lab.badgeConditional', { warns: labResult.counts.warn + labResult.counts.unchecked })}
                          {labResult.verdict === 'rejected' && t('desk.lab.badgeRejected', { reason: (labResult.findings.find((f2) => f2.status === 'block')?.message || '').slice(0, 90) })}
                        </span>
                        <span className="rounded-md bg-violet-500/10 px-2 py-0.5 text-[10px] text-violet-700">{t('desk.lab.modeled')}</span>
                        <button type="button" className="px-1 text-muted-foreground" onClick={() => setLabResult(null)}>×</button>
                      </div>
                      {(labResult.findings || []).some((f2) => f2.status === 'block' || f2.status === 'warn') && (
                        <details className="mt-1">
                          <summary className="cursor-pointer text-[10px] text-muted-foreground">{t('desk.lab.badgeDetails')}</summary>
                          {(labResult.findings || []).filter((f2) => f2.status !== 'ok').map((f2, fi) => (
                            <p key={fi} className="mt-0.5 text-[10px] text-muted-foreground">• {f2.title}: {(f2.message || '').slice(0, 140)}</p>
                          ))}
                        </details>
                      )}
                    </div>
                  )}
                  {(docKind === 'product_tds' || docKind === 'tds_or_report') && !formula.some((l) => l.code) && (
                    <p className="mb-2 rounded-lg bg-muted/60 px-2 py-1 text-[11px] text-muted-foreground" data-testid="desk-no-formula-note">
                      {t('desk.order.noFormulaInDoc')}
                    </p>
                  )}
                  <p className="mb-2 text-[11px] text-muted-foreground">{t('desk.order.formulaHint')}</p>
                  {(() => {
                    const sug = labResult?.lines || null;
                    const sugBy = sug ? Object.fromEntries(sug.map((sl) => [String(sl.code), sl])) : {};
                    const userCodes = new Set(formula.map((x) => String(x.code).trim()).filter(Boolean));
                    const extraSug = sug ? sug.filter((sl) => !userCodes.has(String(sl.code))) : [];
                    const applyRow = (code, pct) => setFormula((prev) => (prev.some((x) => String(x.code).trim() === String(code))
                      ? prev.map((x) => (String(x.code).trim() === String(code) ? { ...x, pct: String(pct) } : x))
                      : [...prev.filter((x) => String(x.code).trim() || String(x.pct).trim()), { code: String(code), pct: String(pct) }]));
                    return (
                      <>
                        {sug && (
                          <div className="mb-1 flex gap-2 text-[10px] font-bold">
                            <span className="w-[13.5rem] text-muted-foreground">{t('desk.lab.colYours')}</span>
                            <span className="text-violet-700">✦ {t('desk.lab.colSug')}</span>
                          </div>
                        )}
                        {formula.map((l, i) => {
                          const m = sug ? sugBy[String(l.code).trim()] : null;
                          return (
                            <div key={i} className="mb-1 flex items-center gap-2">
                              <Input className="h-8 w-28 text-xs" dir="ltr" placeholder="R002" value={l.code} onChange={(e) => setFormula((prev) => prev.map((x, j) => (j === i ? { ...x, code: e.target.value } : x)))} />
                              <Input className="h-8 w-24 text-xs" dir="ltr" placeholder="%" value={l.pct} onChange={(e) => setFormula((prev) => prev.map((x, j) => (j === i ? { ...x, pct: e.target.value } : x)))} />
                              {sug && (m ? (
                                <>
                                  <span className="w-28 rounded-lg border border-dashed border-violet-300 bg-violet-500/5 px-2 py-1.5 font-data text-xs text-violet-700" dir="ltr">{m.code}</span>
                                  <span className="w-20 rounded-lg border border-dashed border-violet-300 bg-violet-500/5 px-2 py-1.5 text-xs font-semibold text-violet-700" dir="ltr">{m.pct}</span>
                                  <Button size="sm" variant="outline" className="h-7 px-2 text-[11px] text-violet-700" data-testid={`desk-sug-use-${i}`} onClick={() => applyRow(m.code, m.pct)}>⇐ {t('desk.lab.useRow')}</Button>
                                </>
                              ) : <span className="px-2 text-[10px] text-muted-foreground">—</span>)}
                            </div>
                          );
                        })}
                        {extraSug.map((sl, si) => (
                          <div key={`sug${si}`} className="mb-1 flex items-center gap-2">
                            <span className="w-28 text-center text-[10px] text-muted-foreground">—</span>
                            <span className="w-24" />
                            <span className="w-28 rounded-lg border border-dashed border-violet-300 bg-violet-500/5 px-2 py-1.5 font-data text-xs text-violet-700" dir="ltr">
                              {sl.code} <span className="rounded bg-violet-600 px-1 text-[9px] text-white">{t('desk.lab.sugNew')}</span>
                            </span>
                            <span className="w-20 rounded-lg border border-dashed border-violet-300 bg-violet-500/5 px-2 py-1.5 text-xs font-semibold text-violet-700" dir="ltr">{sl.pct}</span>
                            <Button size="sm" variant="outline" className="h-7 px-2 text-[11px] text-violet-700" data-testid={`desk-sug-add-${si}`} onClick={() => applyRow(sl.code, sl.pct)}>⇐ {t('desk.lab.useRow')}</Button>
                          </div>
                        ))}
                      </>
                    );
                  })()}
                  <div className="flex items-center gap-2">
                    <Button size="sm" variant="outline" onClick={() => setFormula((p) => [...p, { code: '', pct: '' }])}>+</Button>
                    {formulaSum > 0 && (
                      <span
                        className={`text-[11px] ${Math.abs(formulaSum - 100) > 0.5 ? 'text-destructive' : 'text-success'}`}
                        data-testid="desk-formula-sum"
                      >
                        {t('desk.order.formulaSum', { sum: formulaSum.toFixed(3) })}
                      </span>
                    )}
                  </div>
                  {labResult?.lines && (
                    <div className="mt-1 flex flex-wrap items-center gap-2 text-[11px]" data-testid="desk-sug-actions">
                      <span className="font-semibold text-violet-700">{t('desk.lab.sugSum', { sum: labResult.lines.reduce((a, x) => a + x.pct, 0).toFixed(3) })}</span>
                      <Button size="sm" className="h-7 bg-violet-600 px-2 text-[11px] text-white hover:bg-violet-700" data-testid="desk-sug-useall"
                        onClick={() => { setFormula(labResult.lines.map((x) => ({ code: String(x.code), pct: String(x.pct) }))); setLabResult(null); toast.success(t('desk.lab.sugUsed')); }}>
                        ⇐ {t('desk.lab.useAll')}
                      </Button>
                      <Button size="sm" variant="ghost" className="h-7 px-2 text-[11px]" onClick={() => setLabResult(null)}>{t('desk.lab.sugDismiss')}</Button>
                      <span className="text-[10px] text-muted-foreground">{t('desk.lab.sugRule')}{formula.some((x) => String(x.code).trim()) ? ` ${t('desk.lab.sugPartialHint')}` : ''}</span>
                    </div>
                  )}
                </div>
                {kind !== 'review' && !formula.every((l) => !String(l.code).trim()) && !labOpen && (
                  <button
                    type="button"
                    className="mt-3 w-full rounded-lg border border-violet-300/60 bg-violet-500/5 px-3 py-2 text-start text-xs text-violet-700"
                    data-testid="desk-lab-invite"
                    onClick={() => setLabOpen(true)}
                  >
                    ✦ {t('desk.lab.invite')}
                  </button>
                )}
                {kind !== 'review' && (formula.every((l) => !String(l.code).trim()) || labOpen) && (
                  <div className="mt-3 rounded-xl border border-violet-300/50 bg-violet-500/5 p-3" data-testid="desk-compose">
                    <h3 className="text-xs font-bold text-violet-700">✦ {t('desk.lab.composeTitle')}</h3>
                    <p className="mb-2 text-[11px] text-muted-foreground">{t('desk.lab.composeHint')}</p>
                    <Input
                      className="mb-2 h-8 text-xs" placeholder={t('desk.lab.goalPh')}
                      value={labGoal} onChange={(e) => setLabGoal(e.target.value)} data-testid="desk-compose-goal"
                    />
                    <div className="mb-2 flex items-center gap-1">
                      {['conservative', 'balanced', 'bold'].map((cv) => (
                        <button
                          key={cv} type="button"
                          className={`rounded-full px-3 py-1 text-[11px] ${labCreativity === cv ? 'bg-violet-600 text-white' : 'border border-border text-muted-foreground'}`}
                          onClick={() => setLabCreativity(cv)}
                        >
                          {t(`desk.lab.c_${cv}`)}
                        </button>
                      ))}
                    </div>
                    <Button
                      size="sm" className="bg-violet-600 text-white hover:bg-violet-700"
                      disabled={labBusy || labFilling} onClick={composeRun} data-testid="desk-compose-run"
                    >
                      {labBusy ? t('desk.lab.running') : t('desk.lab.composeBtn')}
                    </Button>
                    {(labBusy || labFilling) && (
                      <div className="mt-2 rounded-xl border border-violet-300/50 bg-card p-3" data-testid="desk-compose-progress">
                        <p className="mb-1 text-[11px] font-medium text-violet-700">
                          {labStep === 'fill' ? t('desk.lab.stepFill') : t('desk.lab.stepCompose')}
                          {etaMedian('compose') ? ` — ${t('desk.order.typicalMeasured', { s: etaMedian('compose') })}` : ''}
                        </p>
                        <div className="flex items-center gap-2"><span className="theater-dot" aria-hidden="true" />
                          <p className="text-xs font-medium text-violet-700">{labFilling ? t('desk.lab.filling') : t('desk.lab.composeProgress')}</p></div>
                        <span className="desk-shimmer mt-2 block h-5 rounded-lg" />
                      </div>
                    )}
                    {labFillReport && labFillReport.unfilled.length > 0 && (
                      <div className="mt-2 rounded-xl border border-warning-border bg-warning-soft p-2 text-[11px]" data-testid="desk-fill-report">
                        <b>{t('desk.lab.fillReport', { filled: labFillReport.filled, left: labFillReport.unfilled.length })}</b>
                        <ul className="ms-4 mt-1 list-disc">
                          {labFillReport.unfilled.slice(0, 8).map((u, ui) => (
                            <li key={ui}><span className="font-data" dir="ltr">{u.code}</span> — {t(`desk.attr.${u.attr}`, { defaultValue: u.attr })}: {t('desk.lab.fillByHand')}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                    {labPre && (
                      <div className="mt-2 rounded-xl border border-warning-border bg-warning-soft p-3 text-xs" data-testid="desk-compose-refused">
                        <b className="mb-1 block">{t('desk.lab.refusedTitle')}</b>
                        <p className="mb-1 text-[11px] text-muted-foreground">{t('desk.lab.refusedHint')}</p>
                        {(labPre.excluded || []).map((x) => (
                          <p key={x.code} className="text-[11px]"><span className="font-data" dir="ltr">{x.code}</span> — {x.name}: {x.missing.map((a) => attrLabel(a)).join(', ')}</p>
                        ))}
                        <Button size="sm" className="mt-2" disabled={labFilling} onClick={fillGapsAndRetry} data-testid="desk-compose-fill">
                          ✦ {labFilling ? t('desk.lab.filling') : t('desk.lab.fillRetry')}
                        </Button>
                      </div>
                    )}
                    {labHouse && labHouse.products > 0 && (
                      <details className="mt-2 rounded-xl border border-border bg-card p-2 text-[11px]" data-testid="desk-compose-house">
                        <summary className="cursor-pointer text-muted-foreground">
                          {t('desk.lab.houseLearn', { n: labHouse.products })}
                        </summary>
                        {(labHouse.cases || []).length > 0 && (
                          <p className="mt-1 text-[11px] text-violet-700" data-testid="desk-cases-line">
                            {t('desk.lab.casesLine', { names: (labHouse.cases || []).join(' · ') })}
                          </p>
                        )}
                        <div className="mt-1" data-testid="desk-teachers">
                          <b className="text-[10px] text-muted-foreground">{t('desk.lab.teachers')}</b>
                          {(labHouse.teachers || []).map((th) => (
                            <div key={th.order_id} className="flex items-center gap-2 py-0.5 text-[11px]">
                              <span className="flex-1 truncate">✓ {th.name}{th.family ? ` — ${th.family}` : ''}</span>
                              <button
                                type="button" className="px-1 text-muted-foreground hover:text-destructive"
                                title={t('desk.lab.teacherExcludeHint')}
                                onClick={() => {
                                  toggleLearning(th.order_id, false);
                                  setLabHouse((prev) => prev && ({ ...prev, products: prev.products - 1, teachers: (prev.teachers || []).filter((x) => x.order_id !== th.order_id) }));
                                }}
                              >×</button>
                            </div>
                          ))}
                        </div>
                        {(labHouse.lessons || []).length > 0 && (
                          <div className="mt-1" data-testid="desk-lessons">
                            <b className="text-[10px] text-muted-foreground">{t('desk.lab.lessonsTitle')}</b>
                            <ul className="ms-4 list-disc space-y-0.5 text-muted-foreground">
                              {labHouse.lessons.map((l, li) => <li key={li} className="font-data text-[10px]" dir="ltr">{l}</li>)}
                            </ul>
                          </div>
                        )}
                        <ul className="ms-4 mt-1 list-disc space-y-0.5 text-muted-foreground">
                          {(labHouse.lines || []).map((l, li) => <li key={li} className="font-data text-[10px]" dir="ltr">{l}</li>)}
                        </ul>
                      </details>
                    )}
                    {labHouse && labHouse.products === 0 && (
                      <p className="mt-2 rounded-xl border border-border bg-muted/40 p-2 text-[11px] text-muted-foreground" data-testid="desk-learn-none">
                        {labHouse.family ? t('desk.lab.learnNoneFam', { family: labHouse.family }) : t('desk.lab.learnNone')}
                      </p>
                    )}
                    {(labCands || []).length > 0 && (
                    <details className="mt-2" data-testid="desk-compose-others">
                      <summary className="cursor-pointer text-[11px] text-muted-foreground">{t('desk.lab.altOthers', { n: (labCands || []).length })}</summary>
                    {(labCands || []).map((cd, ci) => {
                      const tone = cd.verdict === 'rejected' ? 'border-destructive/50 bg-destructive/5'
                        : cd.verdict === 'passed' ? 'border-success/50 bg-success-soft/40' : 'border-warning-border bg-warning-soft/40';
                      return (
                        <div key={ci} className={`mt-2 rounded-xl border p-3 text-xs ${tone}`} data-testid={`desk-compose-cand-${ci}`}>
                          <div className="flex items-center gap-2">
                            <b className="flex-1">{cd.name}</b>
                            {cd.revised && <span className="rounded-md bg-violet-500/10 px-2 py-0.5 text-[10px] text-violet-700">{t('desk.lab.revisedChip')}</span>}
                            <span className="rounded-md bg-violet-500/10 px-2 py-0.5 text-[10px] text-violet-700">{t('desk.lab.modeled')}</span>
                            <span className={`rounded-md px-2 py-0.5 text-[10px] ${cd.verdict === 'rejected' ? 'bg-destructive/10 text-destructive' : cd.verdict === 'passed' ? 'bg-success-soft text-success' : 'bg-warning-soft text-warning'}`}>
                              {t(`desk.lab.v_${cd.verdict}`)}
                            </span>
                          </div>
                          <p className="mt-1 text-muted-foreground">{cd.rationale}</p>
                          <p className="mt-1 font-data text-[10px] text-muted-foreground" dir="ltr">
                            {cd.lines.map((l) => `${l.code} ${l.pct}%`).join(' · ')}
                          </p>
                          <p className="mt-0.5 text-[10px] text-muted-foreground">{t('desk.lab.findings', { ok: cd.counts.ok, warn: cd.counts.warn, block: cd.counts.block, un: cd.counts.unchecked })}</p>
                          {cd.verdict !== 'rejected' && (
                            <Button
                              size="sm" variant="outline" className="mt-1" data-testid={`desk-compose-use-${ci}`}
                              onClick={() => {
                                setLabResult({ name: cd.name, verdict: cd.verdict, counts: cd.counts, findings: cd.findings, solver: cd.solver, rationale: cd.rationale, lines: cd.lines });
                                labLastSug.current = { name: cd.name, lines: cd.lines };
                                setLabCands(null); setLabPre(null);
                                toast.success(t('desk.lab.usedNote'));
                              }}
                            >
                              {t('desk.lab.useThis')}
                            </Button>
                          )}
                        </div>
                      );
                    })}
                    </details>
                    )}
                  </div>
                )}
                <div className="mt-3 border-t border-border pt-3">
                  <Button
                    size="sm" variant="outline" disabled={suggesting} data-testid="desk-suggest"
                    onClick={() => runSuggest()}
                  >
                    ✦ {suggesting ? t('desk.order.suggesting') : t('desk.order.suggest')}
                  </Button>
                  {suggestions && suggestions.length === 0 && (
                    <p className="mt-1 text-[11px] text-muted-foreground">{t('desk.order.suggestNone')}</p>
                  )}
                  {(suggestions || []).map((x) => (
                    <label key={x.item_key} className="mt-2 flex cursor-pointer items-start gap-2 rounded-lg border border-violet-300/50 bg-violet-500/5 p-2 text-xs">
                      <input type="checkbox" checked={!!extraOn[x.item_key]} onChange={(e) => setExtraOn((p) => ({ ...p, [x.item_key]: e.target.checked }))} />
                      <span><b>{x.title}</b>
                        <span className="block text-[11px] text-muted-foreground">{x.why}</span>
                        <span className="block text-[10px] text-violet-600">{t('desk.run.citation')}: {x.source}</span></span>
                    </label>
                  ))}
                </div>
                <Button className="mt-4" disabled={running || (kind === 'review' && docText.trim().length < 40) || (kind === 'review' && docKind === 'out_of_domain')} onClick={startRun} data-testid="desk-run">
                  {t(kind === 'review' ? 'desk.review.run' : 'desk.order.confirmRun')}
                </Button>
                <span className="ms-2 text-[11px] text-muted-foreground">
                  {kind === 'review' ? t('desk.review.hardBlock') : t('desk.order.runsHere')}
                </span>
              </div>
            </div>
          </>
        )}
      </div>
    );
  }

  /* ─────────────── run (rail + gate) ─────────────── */
  if (view === 'run') {
    const findings = run?.findings || [];
    const blocks = kind === 'review'
      ? findings.filter((f) => !f.verdict)
      : findings.filter((f) => f.status === 'block' && f.verdict !== 'fixed');
    const phase = !run ? 'check' : blocks.length ? 'gate' : 'doc';
    const statusOf = (f) => (f.verdict === 'fixed' || f.status === 'ok' ? 'done' : f.status === 'block' ? 'error' : f.status === 'unchecked' ? 'pending' : 'done');
    return (
      <div className="mx-auto max-w-3xl p-6" data-testid="desk-run-view">
        {run?.corpus_outage && (
          <div className="mb-3 rounded-xl border border-destructive/50 bg-destructive/5 p-3 text-xs" data-testid="desk-outage-banner">
            <b className="text-destructive">{t('desk.run.corpusOutage')}</b>
            <p className="mt-0.5 font-data text-[10px] text-muted-foreground" dir="ltr">{String(run.corpus_outage.message).slice(0, 140)}</p>
          </div>
        )}
        <FactoryStrip phase={phase} gate={!!blocks.length} />
        <PipelineRail testId="desk-rail">
          <RailRow status="done" stageKey="materials" label={t('desk.run.materials')}>
            <div className="rounded-xl border border-border bg-card p-3 text-xs">
              <b>{t('desk.run.materials')}</b> — {materials
                ? t('desk.sources.materialsState', materials)
                : t('desk.run.materialsVersion', { v: run?.compute ? (run.compute.materials_version ?? '—') : '…' })}
            </div>
          </RailRow>
          <RailRow
            status={run ? 'done' : 'active'} stageKey="compute" label={t('desk.run.compute')}
            typicalSec={20} micro={run ? undefined : t('desk.run.computeMicro')}
          >
            <div className="rounded-xl border border-border bg-card p-3 text-xs">
              <b>{t('desk.run.compute')}</b>
              {run?.compute && (
                <span className="ms-2 text-muted-foreground">
                  {t('desk.run.computeDone', {
                    sodium: run.compute.values?.sodium_mg?.per_serving ?? '—',
                    sugars: run.compute.values?.sugars_g?.per_serving ?? '—',
                    inc: (run.compute.incompletes || []).length,
                  })}
                </span>
              )}
            </div>
          </RailRow>
          {[...(pack?.checklist || []).filter((c) => !checklistOff.includes(c.item_key)),
            ...selectedUserChecks.map((c) => ({ item_key: c.item_key, title: c.title, lane: c.lane || 'law', extra: true })),
            ...(suggestions || []).filter((x) => extraOn[x.item_key]).map((x) => ({ item_key: x.item_key, title: x.title, lane: 'law', extra: true })),
            ...findings.map((x) => ({ item_key: x.item_key, title: x.title, lane: x.lane || 'own', extra: true }))]
            .filter((c, i, arr) => arr.findIndex((x) => x.item_key === c.item_key) === i)
            .map((c) => {
            const f = findings.find((x) => x.item_key === c.item_key);
            return (
              <RailRow key={c.item_key} status={rerunning ? (rerunning.has(c.item_key) ? 'active' : (f ? statusOf(f) : 'pending')) : (f ? statusOf(f) : 'pending')} stageKey={c.item_key} label={c.title}>
                <div className={`rounded-xl border p-3 text-xs ${f?.status === 'block' && f.verdict !== 'fixed' ? 'border-destructive/50 bg-destructive/5' : 'border-border bg-card'}`}>
                  <div className="flex items-center gap-2">
                    <b className="flex-1">{c.title}</b>
                    <span className={`rounded-full px-2 py-0.5 text-[10px] ${c.lane === 'law' ? 'bg-primary/10 text-primary' : 'bg-success-soft text-success'}`}>
                      {t(c.lane === 'law' ? 'desk.sources.laneLaw' : 'desk.sources.laneOwn')}
                    </span>
                    {f && f.status === 'unchecked' && !f.verdict && (
                      <span className="rounded-full bg-warning-soft px-2 py-0.5 text-[10px] text-warning" data-testid={`desk-wait-${c.item_key}`}>
                        {t('desk.status.uncheckedWait')}
                      </span>
                    )}
                    {f?.fault_kind && (
                      <span className={`rounded-full px-2 py-0.5 text-[10px] ${f.fault_kind === 'data_gap' ? 'bg-warning-soft text-warning' : f.fault_kind === 'formula_fault' ? 'bg-destructive/10 text-destructive' : 'bg-violet-500/10 text-violet-600'}`}>
                        {t(`desk.fault.${kind === 'review' && f.fault_kind === 'formula_fault' ? 'review_formula_fault' : f.fault_kind}`)}
                      </span>
                    )}
                    {f && <span className="text-[10px] text-muted-foreground">{t(`desk.status.${f.verdict === 'fixed' ? 'fixed' : f.status}`)}</span>}
                  </div>
                  {f && <p className="mt-1 leading-6 text-muted-foreground">{f.message || t('desk.run.noMessage')}</p>}
                  {f?.status === 'unchecked' && (
                    <p className="mt-1 text-[10px] text-muted-foreground">{t('desk.status.uncheckedExplain')}</p>
                  )}
                  {f?.citations?.[0] && (
                    <div className="mt-1 rounded-lg border-s-2 border-primary/60 bg-primary/5 p-2 text-[11px]">
                      <b className="block text-[10px] text-primary">{t('desk.run.citation')}</b>
                      {f.citations[0].source} — {f.citations[0].excerpt.slice(0, 160)}…
                    </div>
                  )}
                  {kind === 'review' && f && !signed && (
                    <div className="mt-2">
                      <p className="mb-1 text-[10px] text-muted-foreground">{t('desk.review.voteHint')}</p>
                      <div className="flex flex-wrap gap-2">
                        <Button size="sm" variant={f.verdict === 'accepted' ? 'default' : 'outline'}
                          onClick={() => gate(c.item_key, 'accept')}>
                          {f.verdict === 'accepted' ? '✓ ' : ''}{t('desk.review.voteOk')}
                        </Button>
                        <Button size="sm" variant={f.verdict === 'fixed' ? 'destructive' : 'outline'}
                          onClick={() => gate(c.item_key, 'fix')}>
                          {f.verdict === 'fixed' ? '✓ ' : ''}{t('desk.review.voteFix')}
                        </Button>
                      </div>
                    </div>
                  )}
                  {(kind !== 'review' ? (f?.status === 'block' && f.verdict !== 'fixed')
                    : ((f?.fix_targets || []).length > 0 && !signed)) && (
                    <div className="mt-2">
                      {(f.fix_targets || []).length > 0 && (
                        <div className="mb-2 flex flex-wrap gap-2" data-testid="desk-fix-targets">
                          {f.fix_targets.filter((tg) => tg.kind === 'intake').map((tg) => (
                            <span key={tg.field} className="flex flex-wrap items-center gap-1">
                              {tg.suggested != null && (
                                <Button size="sm" variant="destructive" disabled={!!rerunning}
                                  data-testid={`desk-apply-${c.item_key}`}
                                  onClick={() => gate(c.item_key, 'fix', { [tg.field]: String(tg.suggested) })}>
                                  {rerunning?.has?.(c.item_key)
                                    ? t('desk.gate.applying')
                                    : t('desk.gate.applyGo', { field: targetLabel(tg), value: String(tg.suggested).slice(0, 40) })}
                                </Button>
                              )}
                              <Button size="sm" variant={tg.suggested != null ? 'outline' : 'destructive'}
                                onClick={() => { setMatFix(null); setBatchFix(null); setGateFix({ item_key: c.item_key, field: tg.field, value: String(tg.suggested ?? tg.current ?? ''), locked: true, label: targetLabel(tg) }); }}>
                                {t('desk.gate.editField', { field: targetLabel(tg) })}
                              </Button>
                            </span>
                          ))}
                          {f.fix_targets.filter((tg) => tg.kind === 'material' && !tg.attr).map((tg) => (
                            <Button key={`add-${tg.code}`} size="sm" variant="destructive"
                              data-testid={`desk-add-mat-${tg.code}`}
                              onClick={() => { setGateFix(null); setBatchFix(null); setMapFix(null); setMatFix({ item_key: c.item_key, code: tg.code, name: '', allergen: '' }); }}>
                              {t('desk.gate.addMaterial', { code: tg.code })}
                            </Button>
                          ))}
                          {f.fix_targets.filter((tg) => tg.kind === 'formula_map').map((tg) => (
                            <Button key={`map-${tg.code}`} size="sm" variant="destructive"
                              data-testid={`desk-map-${tg.code}`}
                              onClick={async () => {
                                setGateFix(null); setBatchFix(null); setMatFix(null);
                                setMapFix({ item_key: c.item_key, token: tg.code, choice: '' });
                                if (!matRows) {
                                  try { const r = await api.desk('list_materials'); setMatRows(r.materials || []); }
                                  catch (e) { toast.error(e.message); }
                                }
                              }}>
                              {t('desk.gate.mapLine', { token: String(tg.code).slice(0, 24) })}
                            </Button>
                          ))}
                          {f.fix_targets.some((tg) => tg.kind === 'material' && tg.attr) && (
                            <Button size="sm" variant="destructive"
                              onClick={() => {
                                const rows = f.fix_targets.filter((tg) => tg.kind === 'material' && tg.attr)
                                  .map((tg) => ({ code: tg.code, name: tg.name || tg.code, value: String(tg.suggested ?? '') }));
                                setGateFix(null); setMatFix(null);
                                setBatchFix({ item_key: c.item_key, attr: f.fix_targets.find((tg) => tg.kind === 'material' && tg.attr).attr, rows, suggesting: false });
                              }}>
                              {t('desk.gate.openBatch', { count: f.fix_targets.filter((tg) => tg.kind === 'material' && tg.attr).length })}
                            </Button>
                          )}
                        </div>
                      )}
                      {kind !== 'review' && (
                      <div className="flex flex-wrap gap-3">
                        {(f.fix_targets || []).length > 0 && !extraOpen[c.item_key] ? (
                          <Button size="sm" variant="ghost" className="text-[11px] text-muted-foreground"
                            onClick={() => setExtraOpen((p2) => ({ ...p2, [c.item_key]: true }))}>
                            {t('desk.gate.moreOptions')}
                          </Button>
                        ) : (<>
                        <div className="flex flex-col items-start">
                          <Button size="sm" variant={(f.fix_targets || []).length ? 'outline' : 'destructive'} onClick={() => { setMatFix(null); setBatchFix(null); setGateFix({ item_key: c.item_key, field: schemaFields[0]?.key || '', value: '' }); }}>{t('desk.gate.fix')}</Button>
                          <span className="mt-0.5 text-[10px] text-muted-foreground">{t('desk.gate.fixCap')}</span>
                        </div>
                        <div className="flex flex-col items-start">
                          <Button size="sm" variant="outline" onClick={() => { setMatFix({ item_key: c.item_key, code: '', name: '', allergen: '' }); setGateFix(null); }}>{t('desk.gate.fixMaterials')}</Button>
                          <span className="mt-0.5 text-[10px] text-muted-foreground">{t('desk.gate.fixMatCap')}</span>
                        </div>
                        <div className="flex flex-col items-start">
                          <Button size="sm" variant="outline" onClick={() => gate(c.item_key, 'accept')}>{t('desk.gate.accept')}</Button>
                          <span className="mt-0.5 text-[10px] text-muted-foreground">{t('desk.gate.acceptCap')}</span>
                        </div>
                        <div className="flex flex-col items-start">
                          <Button size="sm" variant="ghost" onClick={() => gate(c.item_key, 'park')}>{t('desk.gate.park')}</Button>
                          <span className="mt-0.5 text-[10px] text-muted-foreground">{t('desk.gate.parkCap')}</span>
                        </div>
                        </>)}
                      </div>
                      )}
                      {gateFix && gateFix.item_key === c.item_key && (
                        <div className="mt-2 rounded-xl border border-border bg-muted/40 p-3" data-testid="desk-gate-fix">
                          <p className="mb-2 text-[11px] text-muted-foreground">{gateFix.locked ? gateFix.label : t('desk.gate.fixWhich')}</p>
                          <div className="flex flex-wrap items-center gap-2">
                            {gateFix.locked ? (
                              <span className="rounded-lg border border-border bg-background px-2 py-1.5 text-xs text-muted-foreground">
                                {schemaFields.find((x) => x.key === gateFix.field)?.label || gateFix.field}
                              </span>
                            ) : (
                            <select
                              className="h-8 rounded-lg border border-border bg-background px-2 text-xs"
                              value={gateFix.field}
                              onChange={(e) => setGateFix({ ...gateFix, field: e.target.value, value: String(fields?.find((x) => x.key === e.target.value)?.value ?? '') })}
                            >
                              {schemaFields.map((sf) => <option key={sf.key} value={sf.key}>{sf.label}</option>)}
                            </select>
                            )}
                            <Input
                              className="h-8 w-56 text-xs"
                              placeholder={String(fields?.find((x) => x.key === gateFix.field)?.value ?? '')}
                              value={gateFix.value}
                              onChange={(e) => setGateFix({ ...gateFix, value: e.target.value })}
                            />
                            <Button size="sm" disabled={!gateFix.value.trim()} onClick={() => { const g = gateFix; setGateFix(null); gate(g.item_key, 'fix', { [g.field]: g.value.trim() }); }}>
                              {t('desk.gate.fixApply')}
                            </Button>
                            <Button size="sm" variant="ghost" onClick={() => setGateFix(null)}>{t('desk.gate.cancel')}</Button>
                          </div>
                        </div>
                      )}
                      {batchFix && batchFix.item_key === c.item_key && (
                        <div className="mt-2 rounded-xl border border-border bg-muted/40 p-3" data-testid="desk-gate-batch">
                          <div className="mb-2 flex items-center gap-2">
                            <p className="flex-1 text-[11px] text-muted-foreground">{t('desk.gate.batchHint', { attr: batchFix.attr })}</p>
                            {(batchFix.attr.startsWith('legal_name') || ESTIMATABLE.includes(batchFix.attr)) && (
                              <Button size="sm" variant="outline" disabled={batchFix.suggesting} onClick={async () => {
                                setBatchFix({ ...batchFix, suggesting: true });
                                try {
                                  const r = await api.desk('suggest_attr_values', { attr: batchFix.attr, codes: batchFix.rows.map((x) => x.code) });
                                  const by = {}; (r.suggestions || []).forEach((x) => { by[x.code] = x.value; });
                                  setBatchFix((p2) => ({ ...p2, suggesting: false, rows: p2.rows.map((x) => ({ ...x, value: x.value || by[x.code] || '', aiValue: by[x.code] || null })) }));
                                } catch (e) { toast.error(e.message); setBatchFix((p2) => ({ ...p2, suggesting: false })); }
                              }}>
                                ✦ {batchFix.suggesting ? t('desk.gate.suggesting') : t('desk.gate.suggestValues')}
                              </Button>
                            )}
                          </div>
                          {batchFix.rows.map((x, i) => (
                            <div key={x.code} className="flex items-center gap-2 border-b border-muted py-1 text-xs last:border-0">
                              <span className="w-16 flex-none font-data" dir="ltr">{x.code}</span>
                              <span className="w-44 flex-none truncate text-muted-foreground">{x.name}</span>
                              <Input className="h-7 flex-1 text-xs" value={x.value}
                                onChange={(e) => setBatchFix((p2) => ({ ...p2, rows: p2.rows.map((y, j) => (j === i ? { ...y, value: e.target.value } : y)) }))} />
                            </div>
                          ))}
                          <div className="mt-2 flex gap-2">
                            <Button size="sm" disabled={batchFix.rows.every((x) => !x.value.trim())} onClick={async () => {
                              const b = batchFix;
                              // dead-air fix: this row is the progress from the first click
                              setRerunning(new Set([b.item_key]));
                              try {
                                for (const row of b.rows.filter((x) => x.value.trim())) {
                                  await api.desk('patch_material', {
                                    code: row.code, attrs: { [b.attr]: row.value.trim() },
                                    // untouched ✦ value = estimate (marked modeled); edited/typed = recorded
                                    modeled: ESTIMATABLE.includes(b.attr) && row.value.trim() === (row.aiValue || ''),
                                  });
                                }
                                setBatchFix(null);
                                await gate(b.item_key, 'fix', {});
                              } catch (e) { toast.error(e.message); setRerunning(null); }
                            }}>{t('desk.gate.batchApply')}</Button>
                            <Button size="sm" variant="ghost" onClick={() => setBatchFix(null)}>{t('desk.gate.cancel')}</Button>
                          </div>
                        </div>
                      )}
                      {mapFix && mapFix.item_key === c.item_key && (
                        <div className="mt-2 rounded-xl border border-border bg-muted/40 p-3" data-testid="desk-gate-map">
                          <p className="mb-2 text-[11px] text-muted-foreground">{t('desk.gate.mapWhich', { token: mapFix.token })}</p>
                          <div className="flex flex-wrap items-center gap-2">
                            <select
                              className="h-8 max-w-64 rounded-lg border border-border bg-background px-2 text-xs"
                              value={mapFix.choice}
                              onChange={(e) => setMapFix({ ...mapFix, choice: e.target.value })}
                            >
                              <option value="">{t('desk.gate.mapPick')}</option>
                              {(matRows || []).map((m) => <option key={m.code} value={m.code}>{m.code} — {m.display_name}</option>)}
                            </select>
                            <Button size="sm" disabled={!mapFix.choice} onClick={() => {
                              const mf = mapFix; setMapFix(null);
                              const mapped = formula
                                .map((l) => ({ code: String(l.code).trim() === mf.token ? mf.choice : String(l.code).trim(), pct: Number(l.pct) }))
                                .filter((l) => l.code && Number.isFinite(l.pct));
                              setFormula(mapped.map((l) => ({ code: l.code, pct: String(l.pct) })));
                              gate(mf.item_key, 'fix', { formula: mapped });
                            }}>{t('desk.gate.mapApply')}</Button>
                            <Button size="sm" variant="ghost" onClick={() => setMapFix(null)}>{t('desk.gate.cancel')}</Button>
                          </div>
                        </div>
                      )}
                      {matFix && matFix.item_key === c.item_key && (
                        <div className="mt-2 rounded-xl border border-border bg-muted/40 p-3" data-testid="desk-gate-mat">
                          <p className="mb-2 text-[11px] text-muted-foreground">{t('desk.gate.matWhich')}</p>
                          <div className="flex flex-wrap items-center gap-2">
                            <Input className="h-8 w-24 text-xs" dir="ltr" placeholder="W001" value={matFix.code} onChange={(e) => setMatFix({ ...matFix, code: e.target.value })} />
                            <Input className="h-8 w-44 text-xs" placeholder={t('desk.gate.matName')} value={matFix.name} onChange={(e) => setMatFix({ ...matFix, name: e.target.value })} />
                            <Input className="h-8 w-36 text-xs" placeholder={t('desk.gate.matAllergen')} value={matFix.allergen} onChange={(e) => setMatFix({ ...matFix, allergen: e.target.value })} />
                            <Button size="sm" disabled={!matFix.code.trim()} onClick={async () => {
                              const m = matFix; setMatFix(null);
                              try {
                                await api.desk('patch_material', { code: m.code.trim(), display_name: m.name.trim(), attrs: m.allergen.trim() ? { allergen: m.allergen.trim() } : {} });
                                await gate(m.item_key, 'fix', {});
                              } catch (e) { toast.error(e.message); }
                            }}>{t('desk.gate.matApply')}</Button>
                            <Button size="sm" variant="ghost" onClick={() => setMatFix(null)}>{t('desk.gate.cancel')}</Button>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                  {kind !== 'review' && f && f.status !== 'block' && !f.verdict
                    && (f.status !== 'ok' || f.fault_kind === 'modeled_dependency') && (
                    <div className="mt-2">
                      <Button size="sm" variant="outline" onClick={() => gate(c.item_key, 'accept')}>
                        {f.fault_kind === 'modeled_dependency' ? t('desk.fault.confirmBtn') : t('desk.gate.accept')}
                      </Button>
                    </div>
                  )}
                </div>
              </RailRow>
            );
          })}
          <RailRow status={run && !blocks.length ? 'active' : 'pending'} stageKey="sign" label={t('desk.run.sign')} isLast>
            <div className="rounded-xl border border-border bg-card p-3 text-xs">
              <b>{t('desk.run.sign')}</b>
              {/* The machine is NOT building here — it waits for the human. Say so. */}
              {run && (() => {
                const waiting = (run.findings || []).filter((x) => x.status === 'unchecked' && !x.verdict).length;
                const estOpen = (run.findings || []).filter((x) => x.fault_kind === 'modeled_dependency' && !x.verdict).length;
                if (estOpen > 0) return <p className="mt-1 text-violet-600">{t('desk.fault.signWaiting', { count: estOpen })}</p>;
                return waiting > 0
                  ? <p className="mt-1 text-warning">{t('desk.run.waitingDecisions', { count: waiting })}</p>
                  : (!blocks.length ? <p className="mt-1 text-muted-foreground">{t('desk.run.waitingYou')}</p> : null);
              })()}
              <p className="mt-1 text-muted-foreground">{t('desk.run.signHint')}</p>
              {run && !blocks.length && !previewDoc && (
                <Button className="mt-2" onClick={openPreview} data-testid="desk-preview">
                  {t('desk.run.previewBtn')}
                </Button>
              )}
              {run && !blocks.length && previewDoc && (
                <p className="mt-1 text-success">{t('desk.run.previewOpen')}</p>
              )}
              {run && blocks.length > 0 && (
                <p className="mt-1 text-destructive">
                  {kind === 'review' ? t('desk.review.votesOpen', { n: blocks.length }) : t('desk.run.blocksOpen', { n: blocks.length })}
                </p>
              )}
            </div>
          </RailRow>
        </PipelineRail>
        {previewDoc && (
          <div className="mt-4" data-testid="desk-preview-doc">
            <DeskPaper artifact={previewDoc} draft />
            <div className="mt-3 flex items-center gap-3">
              <Button onClick={sign} data-testid="desk-sign">
                <CircleCheck className="me-1.5 h-4 w-4" />{t('desk.run.signBtn')}
              </Button>
              <span className="text-[11px] text-muted-foreground">{t('desk.run.signAfterPreview')}</span>
              <Button variant="ghost" size="sm" onClick={() => setPreviewDoc(null)}>{t('desk.gate.cancel')}</Button>
            </div>
          </div>
        )}
        {runParked && (
          <div className="mt-3 rounded-xl border border-warning-border bg-warning-soft p-3 text-xs" data-testid="desk-parked">
            <b>{t('desk.gate.parkedTitle')}</b>
            <p className="mt-0.5 text-muted-foreground">{t('desk.gate.parkedBody')}</p>
            <div className="mt-2 flex gap-2">
              <Button size="sm" variant="outline" onClick={() => { setRunParked(false); }}>{t('desk.gate.parkedStay')}</Button>
              <Button size="sm" variant="ghost" onClick={() => { setRunParked(false); setView('home'); }}>{t('desk.gate.parkedHome')}</Button>
            </div>
          </div>
        )}
        <input ref={matsRef} type="file" accept=".xlsx,.xls" className="hidden" onChange={async (e) => {
          if (!e.target.files[0]) return;
          await importMaterials(e.target.files[0]);
          const rr = await api.desk('run_check', { order_id: orderId });
          setRun(rr);
        }} />
      </div>
    );
  }

  /* ─────────────── bench (scenario A) ─────────────── */
  const doc = benchDoc || signed;
  return (
    <div className="mx-auto max-w-5xl p-6" data-testid="desk-bench">
      <Button variant="ghost" size="sm" className="mb-2" onClick={() => setView('home')}>
        <ArrowLeft className="me-1 h-4 w-4 rtl:rotate-180" />{t('desk.back')}
      </Button>
      {signed && (
        <div className="mb-3 inline-flex rotate-[-2deg] items-center gap-2 rounded-lg border-2 border-success px-4 py-1.5 text-sm font-bold text-success">
          <CircleCheck className="h-4 w-4" />{t('desk.bench.stamp')}
        </div>
      )}
      <div className="grid items-start gap-4 md:grid-cols-[1fr_280px]">
        <div>
          <div className="mb-3 flex flex-wrap gap-2 rounded-xl border border-border bg-card p-2">
            <span className="me-1 self-center text-[10px] font-bold text-muted-foreground">{t('desk.bench.gOut')}</span>
            <Button size="sm" onClick={printDoc}><Printer className="me-1 h-3.5 w-3.5" />{t('desk.bench.printAll')}</Button>
            <Button size="sm" variant="outline" onClick={() => downloadWord(doc)}><FileDown className="me-1 h-3.5 w-3.5" />Word</Button>
            <Button size="sm" variant="outline" onClick={printNft}><Tag className="me-1 h-3.5 w-3.5" />{t('desk.bench.printNft')}</Button>
            <span className="mx-2 w-px bg-border" />
            <span className="me-1 self-center text-[10px] font-bold text-muted-foreground">{t('desk.bench.gNext')}</span>
            <Button size="sm" variant="outline"
              onClick={() => openOrderFromBench(doc?.intake, doc?.order_id ?? null, { kind: 'next', text: t('desk.bench.nextBannerText', { name: doc?.intake?.product_name || '' }) })}>
              {t('desk.bench.nextOrder')}
            </Button>
            <span className="mx-2 w-px bg-border" />
            <span className="me-1 self-center text-[10px] font-bold text-muted-foreground">{t('desk.bench.gBeyond')}</span>
            <Button size="sm" variant="ghost" onClick={() => toast.info(t('desk.bench.roadmap'))}>{t('desk.bench.market')}</Button>
            <Button size="sm" variant="ghost" onClick={() => toast.info(t('desk.bench.roadmap'))}>{t('desk.bench.proto')}</Button>
          </div>
          {doc && doc.order_kind === 'review' && (
            <div className="mb-3 flex flex-wrap gap-2">
              {(doc.findings || []).filter((f) => f.verdict === 'fixed').map((f) => (
                <Button key={f.item_key} size="sm" variant="outline"
                  onClick={() => openOrderFromBench(null, doc?.order_id ?? null, { kind: 'fix', text: `${f.title} — ${f.message}` })}>
                  {t('desk.review.sendToFix', { title: f.title.slice(0, 30) })}
                </Button>
              ))}
            </div>
          )}
          {doc
            ? (doc.order_kind === 'review' ? <ReviewPaper artifact={doc} /> : <DeskPaper artifact={doc} />)
            : <p className="text-sm text-muted-foreground">{t('desk.bench.empty')}</p>}
        </div>
        <div className="rounded-2xl border border-border bg-card p-4 text-xs" data-testid="desk-identity">
          <h3 className="mb-2 text-sm font-bold">{t('desk.bench.idTitle')}</h3>
          {doc && (
            <>
              <p>{t('desk.bench.idSigned', { date: doc.signed_at ? new Date(doc.signed_at).toLocaleDateString() : '—' })}</p>
              <p>{t('desk.bench.idMaterials', { v: doc.materials_version ?? '—' })}</p>
              <p>{t('desk.bench.idChecks', {
                ok: doc.findings.filter((f) => f.status === 'ok' || f.verdict === 'fixed').length,
                total: doc.findings.length,
                cites: doc.findings.reduce((s, f) => s + (f.citations?.length || 0), 0),
              })}</p>
              <p className="mt-1 text-muted-foreground">{t('desk.bench.idIncompletes', { n: (doc.incompletes || []).length })}</p>
            </>
          )}
          <div className="mt-3 border-t border-violet-300/40 pt-3" data-testid="desk-lab">
            <h3 className="mb-1 text-xs font-bold text-violet-700">✦ {t('desk.lab.title')}</h3>
            <p className="mb-2 text-[11px] text-muted-foreground">{t('desk.lab.hint')}</p>
            <p className="mb-1 text-[10px] text-muted-foreground" data-testid="desk-lab-base">
              {t('desk.lab.base', { name: doc?.intake?.product_name || t('desk.lab.baseNone') })}
            </p>
            <Input
              className="mb-2 h-8 text-xs" placeholder={t('desk.lab.goalPh')}
              value={labGoal} onChange={(e) => setLabGoal(e.target.value)} data-testid="desk-lab-goal"
            />
            <div className="mb-2 flex items-center gap-1" data-testid="desk-lab-creativity">
              {['conservative', 'balanced', 'bold'].map((c) => (
                <button
                  key={c} type="button"
                  className={`rounded-full px-3 py-1 text-[11px] ${labCreativity === c ? 'bg-violet-600 text-white' : 'border border-border text-muted-foreground'}`}
                  onClick={() => setLabCreativity(c)}
                >
                  {t(`desk.lab.c_${c}`)}
                </button>
              ))}
              <span className="ms-1 text-[10px] text-muted-foreground">{t('desk.lab.cHint')}</span>
            </div>
            <Button
              size="sm" className="bg-violet-600 text-white hover:bg-violet-700" disabled={labBusy || labGoal.trim().length < 4}
              data-testid="desk-lab-run"
              onClick={async () => {
                setLabBusy(true); setLabCands(null);
                try {
                  const r = await api.desk('suggest_formula', {
                    pack_key: pack.key, goal: labGoal.trim(), creativity: labCreativity,
                    base_formula: (doc?.intake?.formula || []),
                    intake: doc?.intake || {}, corpora: laneSelections,
                  });
                  if (r.refused) {
                    toast.error(t('desk.lab.refusedTitle'));
                    setLabCands([]);
                    return;
                  }
                  setLabHouse(r.house || null);
                  setLabCands(r.candidates || []);
                } catch (e) { toast.error(e.message); } finally { setLabBusy(false); }
              }}
            >
              {labBusy ? t('desk.lab.running') : t('desk.lab.run')}
            </Button>
            {labHouse && labHouse.products > 0 && !labBusy && (
              <p className="mt-1 text-[10px] text-muted-foreground" data-testid="desk-lab-house">
                {t('desk.lab.houseLine', { n: labHouse.products })}
              </p>
            )}
            {labBusy && (
              <div className="mt-2 rounded-xl border border-violet-300/50 bg-violet-500/5 p-3" data-testid="desk-lab-progress">
                <div className="flex items-center gap-2"><span className="theater-dot" aria-hidden="true" />
                  <p className="text-xs font-medium text-violet-700">{t('desk.lab.progress')}</p></div>
                <span className="desk-shimmer mt-2 h-6 rounded-lg" />
                <span className="desk-shimmer mt-1 h-6 rounded-lg" />
              </div>
            )}
            {(labCands || []).map((c, i) => {
              const tone = c.verdict === 'rejected' ? 'border-destructive/50 bg-destructive/5'
                : c.verdict === 'passed' ? 'border-success/50 bg-success-soft/40' : 'border-warning-border bg-warning-soft/40';
              return (
                <div key={i} className={`mt-2 rounded-xl border p-3 text-xs ${tone}`} data-testid={`desk-lab-cand-${i}`}>
                  <div className="flex items-center gap-2">
                    <b className="flex-1">{c.name}</b>
                    <span className="rounded-md bg-violet-500/10 px-2 py-0.5 text-[10px] text-violet-700">{t('desk.lab.modeled')}</span>
                    <span className={`rounded-md px-2 py-0.5 text-[10px] ${c.verdict === 'rejected' ? 'bg-destructive/10 text-destructive' : c.verdict === 'passed' ? 'bg-success-soft text-success' : 'bg-warning-soft text-warning'}`}>
                      {t(`desk.lab.v_${c.verdict}`)}
                    </span>
                  </div>
                  <p className="mt-1 text-muted-foreground">{c.rationale}</p>
                  <p className="mt-1 font-data text-[10px] text-muted-foreground" dir="ltr">
                    {c.lines.map((l) => `${l.code} ${l.pct}%`).join(' · ')}
                  </p>
                  <button
                    type="button" className="mt-1 text-[11px] text-violet-700 underline"
                    onClick={() => setLabOpenFindings((pf) => ({ ...pf, [i]: !pf[i] }))}
                  >
                    {t('desk.lab.findings', { ok: c.counts.ok, warn: c.counts.warn, block: c.counts.block, un: c.counts.unchecked })}
                  </button>
                  {labOpenFindings[i] && (
                    <div className="mt-1 space-y-1">
                      {c.findings.map((f, j) => (
                        <div key={j} className="rounded-lg border border-border bg-card p-2">
                          <b>{f.title}</b> — {f.message}
                          {(f.citations || []).map((cit, k) => (
                            <p key={k} className="mt-0.5 text-[10px] text-muted-foreground">{t('desk.run.citation')}: {cit.source}</p>
                          ))}
                        </div>
                      ))}
                    </div>
                  )}
                  <div className="mt-2">
                    {c.verdict !== 'rejected' ? (
                      <Button size="sm" variant="outline" data-testid={`desk-lab-send-${i}`}
                        onClick={() => openOrderFromBench({ product_name: c.name, formula: c.lines }, doc?.order_id ?? null, { kind: 'next', text: t('desk.lab.sentNote', { goal: labGoal }) })}
                      >
                        {t('desk.lab.send')}
                      </Button>
                    ) : (
                      <span className="text-[10px] text-destructive">{t('desk.lab.binNote')}</span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
          <div className="mt-3 border-t border-border pt-2">
            <h3 className="mb-1 text-xs font-bold">{t('desk.bench.history')}</h3>
            {orders.filter((o) => o.run?.status === 'signed').map((o) => {
              // version = how deep this order sits in its parent chain
              let v = 1; let cur = o;
              while (cur?.parent_order_id) { v += 1; cur = orders.find((x) => x.id === cur.parent_order_id); }
              const active = doc?.order_id === o.id;
              return (
                <div
                  key={o.id}
                  className={`mb-1 flex w-full items-center gap-2 rounded-lg border px-2 py-1.5 text-[11px] ${active ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/50'}`}
                  data-testid={`desk-shelf-${o.id.slice(0, 8)}`}
                >
                  <button
                    type="button" className="flex min-w-0 flex-1 items-center gap-2 text-start"
                    onClick={async () => {
                      setBenchLoading(o.id);
                      try {
                        const st = await api.desk('get_state', { order_id: o.id });
                        if (st.artifact) { setBenchDoc(st.artifact); setSigned(null); }
                        else toast.info(t('desk.bench.noArtifact'));
                      } catch (e) { toast.error(e.message); } finally { setBenchLoading(null); }
                    }}
                  >
                    <span className="flex-1 truncate">{o.kind === 'review' ? '🔍' : '✓'} {o.product_name || o.id.slice(0, 8)}</span>
                    {o.product_family && <span className="flex-none rounded-full bg-primary/10 px-1.5 text-[10px] text-primary">{o.product_family}</span>}
                    <span className="flex-none rounded-full bg-muted px-1.5 text-[10px]">v{v}</span>
                    {benchLoading === o.id && <span className="text-[10px] text-muted-foreground">…</span>}
                  </button>
                  {o.kind !== 'review' && (
                    <button
                      type="button"
                      className={`flex-none rounded-full px-2 py-0.5 text-[10px] ${o.no_learn ? 'bg-muted text-muted-foreground' : 'bg-success-soft text-success'}`}
                      title={t('desk.lab.learnChipHint')}
                      data-testid={`desk-learn-${o.id.slice(0, 8)}`}
                      onClick={() => toggleLearning(o.id, !!o.no_learn)}
                    >
                      {t(o.no_learn ? 'desk.lab.learnChipOff' : 'desk.lab.learnChipOn')}
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
